import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  getPendingGatepasses,
  getGatepassHistory,
  decideGatepass,
  getLocalStudentHistory,
  getHostelOfficeOSPendingGatepasses,
  getHostelOfficeOSGatepassDetails,
  getHostelOfficeOSStudentHistory,
  getHostelOfficeOSGatepassHistory,
  getHostelOfficeOSLogRegister,
  decideHostelOfficeOSGatepass,
  getHostelOfficeSBPendingGatepasses,
  getHostelOfficeSBGatepassDetails,
  getHostelOfficeSBStudentHistory,
  getHostelOfficeSBGatepassHistory,
  getHostelOfficeSBLogRegister,
  decideHostelOfficeSBGatepass,

  getHostelOfficeBGPendingGatepasses,
  getHostelOfficeBGGatepassDetails,
  decideHostelOfficeBGGatepass,
  getHostelOfficeBGLogRegister,
  revertBangaloreStudent,

  getImageUrl,
  getEntryExitLogs,
  getGatepassByNumber,
  getGatepassRegisterLogs,
} from '../api/api';
import PopupBox from '../components/PopupBox';
import ConfirmModal from '../components/ConfirmModal';
import StudentIdCardPopup from '../components/StudentIdCardPopup';
import GatepassStageNotes, { GatepassExtraInfo } from '../components/GatepassStageNotes';
import {
  canDecideOutstationGatepass,
  getApproverHistoryStatus,
  getGatepassStatusLabel,
  getOutstationLifecycleStatus,
} from '../utils/gatepassLifecycle';
import GuardEntryExitTable from '../components/GuardEntryExitTable';
import '../styles/admin.css';

const formatScheduledDateTime = (dateStr, timeStr) => {
  if (!dateStr && !timeStr) return '--';
  let datePart = dateStr || '';
  if (dateStr && dateStr.includes('-')) {
    const parts = dateStr.split('-');
    if (parts.length === 3) datePart = `${parts[2]}/${parts[1]}/${parts[0]}`;
  }
  let timePart = '';
  if (timeStr && timeStr.includes(':')) {
    const tp = timeStr.split(':');
    let h = parseInt(tp[0], 10);
    const m = tp[1] || '00';
    if (!Number.isNaN(h)) {
      const ap = h >= 12 ? 'PM' : 'AM';
      h = h % 12 || 12;
      timePart = `${h}:${m} ${ap}`;
    }
  }
  return [datePart, timePart].filter(Boolean).join(' ') || '--';
};

const HostelOfficePage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activePage, setActivePage] = useState(() => searchParams.get('page') || 'local-requests');

  // Persist activePage to URL
  useEffect(() => {
    setSearchParams({ page: activePage }, { replace: true });
  }, [activePage, setSearchParams]);
  const [viewingGatepass, setViewingGatepass] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [pendingLocalCount, setPendingLocalCount] = useState(0);
  const [pendingOSCount, setPendingOSCount] = useState(0);
  const [pendingSBCount, setPendingSBCount] = useState(0);
  const [pendingBGCount, setPendingBGCount] = useState(0);

  useEffect(() => {
    document.documentElement.classList.add('admin-page-active');
    document.body.classList.add('admin-page-active');
    return () => {
      document.documentElement.classList.remove('admin-page-active');
      document.body.classList.remove('admin-page-active');
    };
  }, []);

  // Fetch pending counts for sidebar badges
  useEffect(() => {
    const fetchPendingCounts = async () => {
      try {
        const [localRes, osRes, sbRes, bgRes] = await Promise.all([
          getPendingGatepasses(),
          getHostelOfficeOSPendingGatepasses(),
          getHostelOfficeSBPendingGatepasses(),
          getHostelOfficeBGPendingGatepasses()
        ]);
        setPendingLocalCount(localRes.data.gatepasses?.length || 0);
        setPendingOSCount(osRes.data.gatepasses?.length || 0);
        setPendingSBCount(sbRes.data.gatepasses?.length || 0);
        setPendingBGCount(bgRes.data.gatepasses?.length || 0);
      } catch (err) {
        console.error('Failed to fetch pending counts', err);
      }
    };
    fetchPendingCounts();
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  const handleNavClick = (pageId) => {
    setActivePage(pageId);
    setViewingGatepass(null);
    setSidebarOpen(false);
  };

  return (
    <div className="admin-layout">
      <header className="admin-header">
        <div className="admin-header-brand">
          <button className="admin-hamburger" onClick={() => setSidebarOpen(!sidebarOpen)}>
            ☰
          </button>
          <div className="admin-header-logo-wrap">
            <span className="admin-header-logo">GoThru</span>
          </div>
        </div>
        <div className="admin-header-right">
          <span className="admin-header-role">Hostel Office</span>
          <button className="admin-logout-btn" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </header>

      {sidebarOpen && <div className="admin-sidebar-overlay" onClick={() => setSidebarOpen(false)} />}

      <div className="admin-body">
        <aside className={`admin-sidebar ${sidebarOpen ? 'mobile-open' : ''}`}>
          <nav className="admin-sidebar-nav">
            {/* Local Gatepass Section */}
            <div className="admin-nav-section-title">Local Gatepass</div>
            <button
              className={`admin-nav-item ${activePage === 'local-requests' ? 'active' : ''}`}
              onClick={() => handleNavClick('local-requests')}
            >
              <svg className="admin-nav-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
              <span className="admin-nav-label">Current Requests</span>
              {pendingLocalCount > 0 && (
                <span className="admin-nav-badge pulse">{pendingLocalCount}</span>
              )}
            </button>
            <button
              className={`admin-nav-item ${activePage === 'local-history' ? 'active' : ''}`}
              onClick={() => handleNavClick('local-history')}
            >
              <svg className="admin-nav-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
              <span className="admin-nav-label">History</span>
            </button>

            {/* Logs Section */}
            <div className="admin-nav-section-title" style={{ marginTop: '20px' }}>Logs</div>
            <button
              className={`admin-nav-item ${activePage === 'log-register' ? 'active' : ''}`}
              onClick={() => handleNavClick('log-register')}
            >
              <svg className="admin-nav-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
                <line x1="16" y1="13" x2="8" y2="13"></line>
                <line x1="16" y1="17" x2="8" y2="17"></line>
                <polyline points="10 9 9 9 8 9"></polyline>
              </svg>
              <span className="admin-nav-label">Log Register</span>
            </button>
            <button
              className={`admin-nav-item ${activePage === 'gatepass-register' ? 'active' : ''}`}
              onClick={() => handleNavClick('gatepass-register')}
            >
              <svg className="admin-nav-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="4" width="18" height="16" rx="2" ry="2"></rect>
                <path d="M3 10h18"></path>
                <path d="M8 2v4"></path>
                <path d="M16 2v4"></path>
                <path d="M8 14h3"></path>
                <path d="M13 14h3"></path>
              </svg>
              <span className="admin-nav-label">Gatepass Register</span>
            </button>

            {/* Outstation Gatepass Section */}
            <div className="admin-nav-section-title" style={{ marginTop: '20px' }}>Outstation Gatepass</div>
            <button
              className={`admin-nav-item ${activePage === 'os-requests' ? 'active' : ''}`}
              onClick={() => handleNavClick('os-requests')}
            >
              <svg className="admin-nav-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
              <span className="admin-nav-label">Current Requests</span>
              {pendingOSCount > 0 && (
                <span className="admin-nav-badge pulse">{pendingOSCount}</span>
              )}
            </button>
            <button
              className={`admin-nav-item ${activePage === 'os-history' ? 'active' : ''}`}
              onClick={() => handleNavClick('os-history')}
            >
              <svg className="admin-nav-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
              <span className="admin-nav-label">History</span>
            </button>
            <button
              className={`admin-nav-item ${activePage === 'os-log-register' ? 'active' : ''}`}
              onClick={() => handleNavClick('os-log-register')}
            >
              <svg className="admin-nav-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
              </svg>
              <span className="admin-nav-label">Log Register</span>
            </button>

            {/* Mid/End Sem Break Section */}
            <div className="admin-nav-section-title" style={{ marginTop: '20px' }}>Mid/End Sem Break</div>
            <button
              className={`admin-nav-item ${activePage === 'sb-requests' ? 'active' : ''}`}
              onClick={() => handleNavClick('sb-requests')}
            >
              <svg className="admin-nav-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="16" y1="2" x2="16" y2="6"></line>
                <line x1="8" y1="2" x2="8" y2="6"></line>
                <line x1="3" y1="10" x2="21" y2="10"></line>
              </svg>
              <span className="admin-nav-label">Current Requests</span>
              {pendingSBCount > 0 && (
                <span className="admin-nav-badge pulse">{pendingSBCount}</span>
              )}
            </button>
            <button
              className={`admin-nav-item ${activePage === 'sb-history' ? 'active' : ''}`}
              onClick={() => handleNavClick('sb-history')}
            >
              <svg className="admin-nav-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
              <span className="admin-nav-label">History</span>
            </button>
            <button
              className={`admin-nav-item ${activePage === 'sb-log-register' ? 'active' : ''}`}
              onClick={() => handleNavClick('sb-log-register')}
            >
              <svg className="admin-nav-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
              </svg>
              <span className="admin-nav-label">Log Register</span>
            </button>

            {/* Bangalore (3rd Year Transfer) Section */}
            <div className="admin-nav-section-title" style={{ marginTop: '20px' }}>Bangalore Gatepass</div>
            <button
              className={`admin-nav-item ${activePage === 'bg-requests' ? 'active' : ''}`}
              onClick={() => handleNavClick('bg-requests')}
            >
              <svg className="admin-nav-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 21h18"></path><path d="M5 21V7l7-4 7 4v14"></path>
              </svg>
              <span className="admin-nav-label">Current Requests</span>
              {pendingBGCount > 0 && (
                <span className="admin-nav-badge pulse">{pendingBGCount}</span>
              )}
            </button>
            <button
              className={`admin-nav-item ${activePage === 'bg-log-register' ? 'active' : ''}`}
              onClick={() => handleNavClick('bg-log-register')}
            >
              <svg className="admin-nav-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
              </svg>
              <span className="admin-nav-label">Log Register</span>
            </button>
          </nav>
        </aside>

        <main className="admin-main">
          {activePage !== 'log-register' && activePage !== 'gatepass-register' && (
            <div className="admin-college-banner">
              <h1 className="admin-college-name">RAJIV GANDHI INSTITUTE OF PETROLEUM TECHNOLOGY</h1>
              <p className="admin-college-subtitle">(An Institute of National Importance Established Under an Act of Parliament)</p>
              <h2 className="admin-college-name-hi">राजीव गाँधी पेट्रोलियम प्रौद्योगिकी संस्थान</h2>
              <p className="admin-college-subtitle-hi">(संसद के अधिनियम द्वारा राष्ट्रीय महत्व का संस्थान)</p>
            </div>
          )}

          <div className={`os-content-area${activePage === 'log-register' || activePage === 'gatepass-register' ? ' os-content-fill' : ''}`}>
            {/* Local Gatepass Views */}
            {activePage === 'local-requests' && !viewingGatepass && (
              <LocalRequestsView onViewDetails={setViewingGatepass} />
            )}
            {activePage === 'local-requests' && viewingGatepass && (
              <LocalGatepassDetailsView gatepassId={viewingGatepass} onBack={() => setViewingGatepass(null)} />
            )}
            {activePage === 'local-history' && <LocalHistoryView />}
            {activePage === 'log-register' && <LogRegisterView />}
            {activePage === 'gatepass-register' && <GatepassRegisterView />}

            {/* Outstation Gatepass Views */}
            {activePage === 'os-requests' && !viewingGatepass && (
              <OSRequestsView onViewDetails={setViewingGatepass} />
            )}
            {activePage === 'os-requests' && viewingGatepass && (
              <OSGatepassDetailsView gatepassId={viewingGatepass} onBack={() => setViewingGatepass(null)} />
            )}
            {activePage === 'os-history' && <OSHistoryView />}
            {activePage === 'os-log-register' && <GatepassDecisionLogRegisterView
              title="Outstation Gatepass Log Register"
              gatepassPrefix="OS"
              downloadName="OutstationGatepassLogRegister"
              fetchRegister={getHostelOfficeOSLogRegister}
              enableScheduledDateRange
            />}

            {/* Mid/End Sem Break Gatepass Views */}
            {activePage === 'sb-requests' && !viewingGatepass && (
              <SBRequestsView onViewDetails={setViewingGatepass} />
            )}
            {activePage === 'sb-requests' && viewingGatepass && (
              <SBGatepassDetailsView gatepassId={viewingGatepass} onBack={() => setViewingGatepass(null)} />
            )}
            {activePage === 'sb-history' && <SBHistoryView />}
            {activePage === 'sb-log-register' && <GatepassDecisionLogRegisterView
              title="Mid/End Sem Break Log Register"
              gatepassPrefix="SB"
              downloadName="MidEndSemBreakLogRegister"
              fetchRegister={getHostelOfficeSBLogRegister}
            />}

            {/* Bangalore Gatepass Views */}
            {activePage === 'bg-requests' && !viewingGatepass && (
              <BGRequestsView onViewDetails={setViewingGatepass} />
            )}
            {activePage === 'bg-requests' && viewingGatepass && (
              <BGGatepassDetailsView gatepassId={viewingGatepass} onBack={() => setViewingGatepass(null)} />
            )}
            {activePage === 'bg-log-register' && <BGLogRegisterView />}
          </div>
          <div className="admin-support-footer">If any queries mail to gothrurgipt@rgipt.ac.in</div>
        </main>
      </div>
    </div>
  );
};

// ==================== LOCAL GATEPASS VIEWS ====================

const LocalRequestsView = ({ onViewDetails }) => {
  const [gatepasses, setGatepasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // ID Card popup state
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showIdCard, setShowIdCard] = useState(false);

  const handleProfileClick = (student, gatepass) => {
    setSelectedStudent({
      ...student,
      name: gatepass.studentName,
      rollnumber: gatepass.rollnumber,
      branch: gatepass.branch || gatepass.department,
      department: gatepass.department,
      contact: gatepass.contact,
      roomNumber: gatepass.roomNumber,
      hostelName: gatepass.hostelName,
      course: gatepass.course,
      semester: gatepass.semester,
    });
    setShowIdCard(true);
  };

  const fetchGatepasses = async () => {
    try {
      setError('');
      const res = await getPendingGatepasses();
      setGatepasses(res.data.gatepasses || []);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to fetch gatepasses');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGatepasses();
  }, []);

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    let hours = d.getHours();
    const mins = String(d.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    return `${day}/${month}/${year},  ${hours}:${mins} ${ampm}`;
  };

  const sortedGatepasses = [...gatepasses].sort((a, b) =>
    new Date(a.createdAt) - new Date(b.createdAt)
  );

  return (
    <div className="os-section">
      <h2 className="os-section-title">Pending Local Gatepass Requests</h2>

      {loading && <div className="os-loading">Loading requests...</div>}
      {error && <div className="os-error">{error}</div>}

      {!loading && gatepasses.length === 0 && (
        <div className="os-empty">No pending local gatepass requests</div>
      )}

      <div className="os-cards-grid">
        {sortedGatepasses.map((gp) => (
          <div key={gp._id} className="os-request-card">
            <div
              className="os-card-avatar profile-pic-hover"
              onClick={() => handleProfileClick(gp.student, gp)}
              title="Click to view GoThru ID Card"
            >
              {gp.student?.imageUrl ? (
                <img
                  src={getImageUrl(gp.student.imageUrl)}
                  alt={gp.studentName}
                  className="os-card-avatar-img"
                />
              ) : (
                <span style={{ fontSize: '20px', color: '#fff' }}>●</span>
              )}
            </div>
            <div className="os-card-info">
              <div className="os-card-name">{gp.studentName}</div>
              <div className="os-card-details">
                <span>{gp.rollnumber}</span>
              </div>
              <div className="os-card-branch">{gp.place}</div>
              <div className="os-card-applied">Applied: {formatDateTime(gp.createdAt)}</div>
            </div>
            <button className="os-view-btn" onClick={() => onViewDetails(gp._id)}>
              View Details
            </button>
          </div>
        ))}
      </div>

      {/* Student ID Card Popup */}
      <StudentIdCardPopup
        student={selectedStudent}
        isOpen={showIdCard}
        onClose={() => setShowIdCard(false)}
      />
    </div>
  );
};

const LocalGatepassDetailsView = ({ gatepassId, onBack }) => {

  const [gatepass, setGatepass] = useState(null);
  const [studentHistory, setStudentHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [popupMessage, setPopupMessage] = useState('');
  const [deciding, setDeciding] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ open: false, decision: null });
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');

  // ID Card popup state
  const [showIdCard, setShowIdCard] = useState(false);
  const [showLocalDocPopup, setShowLocalDocPopup] = useState(false);

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        setError('');
        const res = await getPendingGatepasses();
        const allGatepasses = res.data.gatepasses || [];

        const found = allGatepasses.find(gp => gp._id === gatepassId);
        setGatepass(found || null);

        // Fetch student history if gatepass found
        if (found?.student) {
          setHistoryLoading(true);
          try {
            // Handle both populated object and plain ID
            const studentId = typeof found.student === 'object' ? found.student._id : found.student;
            const historyRes = await getLocalStudentHistory(studentId);
            setStudentHistory(historyRes.data.gatepasses || []);
          } catch (histErr) {
            console.error('Failed to fetch student history:', histErr);
          } finally {
            setHistoryLoading(false);
          }
        }
      } catch (err) {
        setError(err?.response?.data?.message || 'Failed to fetch details');
      } finally {
        setLoading(false);
      }
    };
    fetchDetails();
  }, [gatepassId]);

  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const day = d.getDate();
    const month = monthNames[d.getMonth()];
    let hours = d.getHours();
    const mins = String(d.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    return `${day} ${month}, ${hours}:${mins} ${ampm}`;
  };

  const formatTime12hr = (timeStr) => {
    if (!timeStr) return '';
    const parts = timeStr.split(':');
    if (parts.length < 2) return timeStr;
    let hours = parseInt(parts[0], 10);
    const minutes = parts[1];
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    return `${hours}:${minutes} ${ampm}`;
  };

  const formatDateTimeFromParts = (dateStr, timeStr) => {
    if (!dateStr || !timeStr) return '--';
    // dateStr is like "2026-01-17", timeStr is like "12:06"
    const dateParts = dateStr.split('-');
    if (dateParts.length !== 3) return '--';
    const [, monthNum, day] = dateParts;
    const month = monthNames[parseInt(monthNum, 10) - 1];
    const formattedTime = formatTime12hr(timeStr);
    return `${parseInt(day, 10)} ${month} ${formattedTime}`;
  };

  const openConfirmModal = (decision) => {
    if (decision === 'denied') {
      setShowRejectModal(true);
    } else {
      setConfirmModal({ open: true, decision });
    }
  };

  const closeConfirmModal = () => {
    setConfirmModal({ open: false, decision: null });
  };

  const closeRejectModal = () => {
    setShowRejectModal(false);
    setRejectionReason('');
  };

  const handleRejectConfirm = async () => {
    const trimmedReason = rejectionReason.trim();
    if (!trimmedReason) {
      setPopupMessage('Please enter a reason for rejection');
      return;
    }

    setDeciding(true);
    try {
      const res = await decideGatepass({ gatepassId, decision: 'denied', rejectionReason: trimmedReason });
      closeRejectModal();
      setPopupMessage(res.data.message);
      setTimeout(() => onBack(), 1500);
    } catch (err) {
      setPopupMessage(err?.response?.data?.message || 'Failed to process decision');
      closeRejectModal();
    } finally {
      setDeciding(false);
    }
  };

  const handleDecision = async (decision) => {
    setDeciding(true);
    try {
      const res = await decideGatepass({ gatepassId, decision });
      closeConfirmModal();
      setPopupMessage(res.data.message);
      setTimeout(() => onBack(), 1500);
    } catch (err) {
      setPopupMessage(err?.response?.data?.message || 'Failed to process decision');
      closeConfirmModal();
    } finally {
      setDeciding(false);
    }
  };

  if (loading) return <div className="os-loading">Loading details...</div>;
  if (error) return <div className="os-error">{error}</div>;
  if (!gatepass) return <div className="os-error">Gatepass not found</div>;

  return (
    <div className="os-details-view">
      <button className="os-back-btn" onClick={onBack}>
        ← Back to Requests
      </button>

      <h2 className="os-section-title">Local Gatepass Details</h2>

      <div className="os-details-card">
        {/* Student Image and Name */}
        <div className="os-student-info-header">
          <div
            className="os-student-avatar-large profile-pic-hover"
            onClick={() => setShowIdCard(true)}
            title="Click to view GoThru ID Card"
          >
            {gatepass.student?.imageUrl ? (
              <img
                src={getImageUrl(gatepass.student.imageUrl)}
                alt={gatepass.studentName}
              />
            ) : (
              <span className="os-avatar-placeholder">{gatepass.studentName?.charAt(0)?.toUpperCase() || '?'}</span>
            )}
          </div>
          <div className="os-student-header-info">
            <h3 className="os-student-name-large">{gatepass.studentName}</h3>
            <span className="os-student-roll-large">{gatepass.rollnumber}</span>
          </div>
        </div>

        <div className="os-details-grid">
          <div className="os-detail-item">
            <span className="os-detail-label">Place</span>
            <span className="os-detail-value">{gatepass.place}</span>
          </div>
          <div className="os-detail-item">
            <span className="os-detail-label">Purpose</span>
            <span className="os-detail-value">{gatepass.purpose}</span>
          </div>
          <div className="os-detail-item">
            <span className="os-detail-label">Course</span>
            <span className="os-detail-value">{gatepass.course || '--'}</span>
          </div>
          <div className="os-detail-item">
            <span className="os-detail-label">Semester</span>
            <span className="os-detail-value">{gatepass.semester || '--'}</span>
          </div>
          <div className="os-detail-item">
            <span className="os-detail-label">Requested Exit</span>
            <span className="os-detail-value">{formatDateTimeFromParts(gatepass.dateOut, gatepass.timeOut)}</span>
          </div>
          <div className="os-detail-item">
            <span className="os-detail-label">Requested Return</span>
            <span className="os-detail-value">{formatDateTimeFromParts(gatepass.dateIn, gatepass.timeIn)}</span>
          </div>
        </div>

        {gatepass.supportingDocument && (
          <div className="os-detail-full" style={{ marginTop: '16px' }}>
            <span className="os-detail-label">Supporting Document</span>
            <div style={{ marginTop: '8px' }}>
              <button
                className="os-view-doc-btn"
                onClick={() => setShowLocalDocPopup(true)}
              >
                Preview Supporting Image
              </button>
            </div>
          </div>
        )}

        {/* Approval Timeline */}
        <div className="os-approval-timeline">
          <h4>Approval Timeline</h4>
          <div className="os-timeline-item">
            <span className="os-timeline-label">Student Applied</span>
            <span className="os-timeline-value">{formatDateTime(gatepass.createdAt)}</span>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="os-action-btns">
          <button
            className="os-reject-btn"
            onClick={() => openConfirmModal('denied')}
            disabled={deciding}
          >
            Reject
          </button>
          <button
            className="os-approve-btn"
            onClick={() => openConfirmModal('approved')}
            disabled={deciding}
          >
            Approve
          </button>
        </div>
      </div>

      {/* Student's Local Gatepass History */}
      <div className="os-student-history-section">
        <h3 className="os-history-title">Student's Previous Local Gatepasses</h3>
        {historyLoading && <div className="os-loading">Loading history...</div>}
        {!historyLoading && studentHistory.length === 0 && (
          <div className="os-empty">No previous local gatepasses</div>
        )}
        {!historyLoading && studentHistory.length > 0 && (
          <div className="os-history-table local-history-table">
            <div className="local-history-header">
              <span>GP No.</span>
              <span>Status</span>
              <span>Place</span>
              <span>Requested Out</span>
              <span>Requested In</span>
              <span>Actual Out</span>
              <span>Actual In</span>
            </div>
            {studentHistory.map((gp) => (
              <div key={gp._id} className={`local-history-row ${gp.status}`}>
                <span className="local-history-gpno">{gp.gatePassNo || '--'}</span>
                <span className={`local-history-status ${gp.status}`}>
                  {gp.status === 'approved' ? 'Approved' : gp.status === 'denied' ? 'Denied' : gp.status}
                </span>
                <span className="local-history-place">{gp.place || '--'}</span>
                <span className="local-history-time">{formatDateTimeFromParts(gp.dateOut, gp.timeOut)}</span>
                <span className="local-history-time">{formatDateTimeFromParts(gp.dateIn, gp.timeIn)}</span>
                <span className="local-history-time">{gp.actualExitAt ? formatDateTime(gp.actualExitAt) : '--'}</span>
                <span className="local-history-time">{gp.actualEntryAt ? formatDateTime(gp.actualEntryAt) : '--'}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={confirmModal.open}
        onClose={closeConfirmModal}
        onConfirm={() => handleDecision(confirmModal.decision)}
        decision={confirmModal.decision}
        studentName={gatepass?.studentName}
        rollNumber={gatepass?.rollnumber}
        isLocal={true}
        place={gatepass?.place}
        purpose={gatepass?.purpose}
        isProcessing={deciding}
      />

      {showRejectModal && (
        <div className="doc-popup-overlay" onClick={closeRejectModal}>
          <div className="doc-popup-content" onClick={(e) => e.stopPropagation()}>
            <button className="doc-popup-close" onClick={closeRejectModal}>×</button>
            <h3 style={{ marginBottom: '16px' }}>Reject Local Gatepass</h3>
            <p style={{ marginBottom: '12px' }}>Please provide a rejection reason before continuing.</p>
            <textarea
              className="rejection-reason-input"
              placeholder="Enter reason for rejection..."
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              rows={4}
              disabled={deciding}
            />
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '18px' }}>
              <button
                className="os-reject-btn"
                onClick={closeRejectModal}
                disabled={deciding}
                style={{ minWidth: '100px' }}
              >
                Cancel
              </button>
              <button
                className="os-approve-btn"
                onClick={handleRejectConfirm}
                disabled={deciding}
                style={{ minWidth: '160px', background: '#dc3545', borderColor: '#dc3545' }}
              >
                {deciding ? 'Processing...' : 'Confirm Rejection'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showLocalDocPopup && gatepass.supportingDocument && (
        <div className="doc-popup-overlay" onClick={() => setShowLocalDocPopup(false)}>
          <div className="doc-popup-content" onClick={(e) => e.stopPropagation()}>
            <button className="doc-popup-close local-doc-popup-close" onClick={() => setShowLocalDocPopup(false)}>×</button>
            <img
              className="doc-popup-image"
              src={getImageUrl(gatepass.supportingDocument)}
              alt="Local Gatepass Supporting Document"
            />
          </div>
        </div>
      )}

      {/* Student ID Card Popup */}
      <StudentIdCardPopup
        student={gatepass ? {
          ...gatepass.student,
          name: gatepass.studentName,
          rollnumber: gatepass.rollnumber,
          branch: gatepass.branch || gatepass.department,
          department: gatepass.department,
          contact: gatepass.contact,
          roomNumber: gatepass.roomNumber,
          hostelName: gatepass.hostelName,
        } : null}
        isOpen={showIdCard}
        onClose={() => setShowIdCard(false)}
      />

      <PopupBox message={popupMessage} onClose={() => setPopupMessage('')} />
    </div>
  );
};

const LocalHistoryView = () => {
  const [gatepasses, setGatepasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  // Popup state
  const [showPopup, setShowPopup] = useState(false);
  const [popupData, setPopupData] = useState(null);
  const [popupLoading, setPopupLoading] = useState(false);

  // ID Card popup state
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showIdCard, setShowIdCard] = useState(false);

  const handleProfileClick = (e, student, gp) => {
    e.stopPropagation(); // Prevent row click
    setSelectedStudent({
      ...student,
      name: gp.studentName,
      rollnumber: gp.rollnumber,
      branch: gp.branch || gp.department,
      department: gp.department,
      contact: gp.contact,
      roomNumber: gp.roomNumber,
      hostelName: gp.hostelName,
      course: gp.course,
      semester: gp.semester,
    });
    setShowIdCard(true);
  };

  const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

  const fetchHistory = async (searchQuery = '') => {
    try {
      setError('');
      setLoading(true);
      const res = await getGatepassHistory(searchQuery);
      setGatepasses(res.data.gatepasses || []);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to fetch history');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchHistory(search);
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    const datePart = d.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
    const timePart = d.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
    return `${datePart}, ${timePart}`;
  };

  const handleRowClick = async (gp) => {
    if (!gp._id) return;

    setShowPopup(true);
    setPopupLoading(false);
    // Use the row data directly since it includes all needed info
    setPopupData({
      student: {
        ...gp.student,
        name: gp.studentName,
        rollnumber: gp.rollnumber,
        course: gp.course || gp.student?.course,
        branch: gp.branch || gp.student?.branch || gp.department || gp.student?.department,
        department: gp.department || gp.student?.department,
        roomNumber: gp.roomNumber || gp.student?.roomNumber,
        hostelName: gp.hostelName || gp.student?.hostelName,
        contact: gp.contact || gp.student?.contactNumber,
        contactNumber: gp.contact || gp.student?.contactNumber,
        presence: gp.student?.presence,
      },
      gatePassNo: gp.gatePassNo,
      gatepassDetails: {
        purpose: gp.purpose,
        place: gp.place,
        status: gp.status,
        dateOut: gp.dateOut,
        dateIn: gp.dateIn,
        timeOut: gp.timeOut,
        timeIn: gp.timeIn,
        actualExitAt: gp.actualExitAt,
        actualEntryAt: gp.actualEntryAt,
        appliedAt: gp.createdAt,
        approvedAt: gp.decidedAt,
        rejectionReason: gp.rejectionReason,
      }
    });
  };

  const closePopup = () => {
    setShowPopup(false);
    setPopupData(null);
  };

  return (
    <div className="os-section">
      <div className="os-history-header-row">
        <h2 className="os-section-title">Local Gatepass History</h2>
        <form onSubmit={handleSearch} className="os-search-form">
          <input
            type="text"
            placeholder="Search by name or roll number..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="os-search-input"
          />
          <button type="submit" className="os-search-btn">🔍</button>
        </form>
      </div>

      {loading && <div className="os-loading">Loading history...</div>}
      {error && <div className="os-error">{error}</div>}

      {!loading && gatepasses.length === 0 && (
        <div className="os-empty">No history found</div>
      )}

      <div className="os-history-table">
        {gatepasses.map((gp) => {
          const hasGatepassNo = !!gp.gatePassNo;
          const isClickable = hasGatepassNo;
          const status = gp.status || 'pending';

          return (
            <div
              key={gp._id}
              className={`os-history-row-enhanced ${status} ${isClickable ? 'os-history-row-clickable' : ''}`}
              onClick={() => isClickable && handleRowClick(gp)}
              role={isClickable ? 'button' : undefined}
              tabIndex={isClickable ? 0 : undefined}
            >
              {/* Student Avatar */}
              <div
                className="os-history-avatar profile-pic-hover"
                onClick={(e) => handleProfileClick(e, gp.student, gp)}
                title="Click to view GoThru ID Card"
              >
                {gp.student?.imageUrl ? (
                  <img src={`${API_BASE}${gp.student.imageUrl}`} alt="" />
                ) : (
                  <div className="os-history-avatar-placeholder">
                    {gp.studentName?.charAt(0) || '?'}
                  </div>
                )}
              </div>

              {/* Student Info */}
              <div className="os-history-student-info">
                <div className="os-history-name">{gp.studentName}</div>
                <div className="os-history-meta">{gp.rollnumber}</div>
              </div>

              {/* Gatepass Number */}
              {hasGatepassNo ? (
                <span className="os-history-gp-number">{gp.gatePassNo}</span>
              ) : status !== 'rejected' ? (
                <span className="os-history-gp-pending">Yet to be Approved</span>
              ) : null}

              {/* Leave Info */}
              <div className="os-history-leave-enhanced">
                <span>{gp.place}</span>
                <span className="os-history-reason-text">{gp.purpose}</span>
              </div>

              {/* Status Badge */}
              <div className={`os-status-badge ${status}`}>
                {status?.toUpperCase()}
              </div>
            </div>
          );
        })}
      </div>

      {/* Gatepass Detail Popup */}
      {showPopup && (
        <div className="gatepass-popup-overlay" onClick={closePopup}>
          <div className="gatepass-popup-content" onClick={(e) => e.stopPropagation()}>
            <div className="gatepass-popup-header">
              <h3>Local Gatepass Details</h3>
              <button className="gatepass-popup-close" onClick={closePopup}>×</button>
            </div>
            <div className="gatepass-popup-body">
              {popupLoading && <div className="os-loading">Loading gatepass details...</div>}
              {!popupLoading && popupData?.error && (
                <div className="os-error">{popupData.error}</div>
              )}
              {!popupLoading && popupData && !popupData.error && (
                <>
                  <div className="gatepass-popup-student">
                    <div
                      className="gatepass-popup-avatar profile-pic-hover"
                      onClick={() => {
                        setSelectedStudent(popupData.student);
                        setShowIdCard(true);
                      }}
                      title="Click to view GoThru ID Card"
                    >
                      {popupData.student?.imageUrl ? (
                        <img src={`${API_BASE}${popupData.student.imageUrl}`} alt="" />
                      ) : (
                        <div className="gatepass-popup-avatar-placeholder">
                          {popupData.student?.name?.charAt(0) || '?'}
                        </div>
                      )}
                    </div>
                    <div className="gatepass-popup-student-info">
                      <h4>{popupData.student?.name}</h4>
                      <p>{popupData.student?.rollnumber} • {popupData.student?.department}</p>
                    </div>
                  </div>

                  <div className="gatepass-popup-badges">
                    <span className="gatepass-popup-type-badge local">LOCAL</span>
                    <span className="gatepass-popup-number-badge">{popupData.gatePassNo}</span>
                    <span className={`gatepass-popup-status-badge ${popupData.gatepassDetails?.status}`}>
                      {popupData.gatepassDetails?.status || 'Pending'}
                    </span>
                  </div>

                  <div className="gatepass-popup-details">
                    <div className="gatepass-popup-detail-item">
                      <span className="gatepass-popup-detail-label">Purpose</span>
                      <span className="gatepass-popup-detail-value">{popupData.gatepassDetails?.purpose || '--'}</span>
                    </div>
                    <div className="gatepass-popup-detail-item">
                      <span className="gatepass-popup-detail-label">Place</span>
                      <span className="gatepass-popup-detail-value">{popupData.gatepassDetails?.place || '--'}</span>
                    </div>
                  </div>

                  <div className="gatepass-popup-utilization">
                    <h5>Scheduled Times</h5>
                    <div className="gatepass-popup-util-grid">
                      <div className="gatepass-popup-util-item exit">
                        <span className="gatepass-popup-util-label">Scheduled Exit</span>
                        <span className="gatepass-popup-util-value scheduled">
                          {popupData.gatepassDetails?.dateOut && popupData.gatepassDetails?.timeOut
                            ? (() => {
                                const parts = popupData.gatepassDetails.dateOut.split('-');
                                const tp = popupData.gatepassDetails.timeOut.split(':');
                                let h = tp.length >= 2 ? parseInt(tp[0], 10) : 0;
                                const m = tp.length >= 2 ? tp[1] : '00';
                                const ap = h >= 12 ? 'PM' : 'AM';
                                h = h % 12 || 12;
                                const time12 = `${h}:${m} ${ap}`;
                                if (parts.length === 3) {
                                  return `${parts[2]}/${parts[1]}/${parts[0]} ${time12}`;
                                }
                                return `${popupData.gatepassDetails.dateOut} ${time12}`;
                              })()
                            : popupData.gatepassDetails?.dateOut || '--'}
                        </span>
                      </div>
                      <div className="gatepass-popup-util-item entry">
                        <span className="gatepass-popup-util-label">Scheduled Entry</span>
                        <span className="gatepass-popup-util-value scheduled">
                          {popupData.gatepassDetails?.dateIn && popupData.gatepassDetails?.timeIn
                            ? (() => {
                                const parts = popupData.gatepassDetails.dateIn.split('-');
                                const tp = popupData.gatepassDetails.timeIn.split(':');
                                let h = tp.length >= 2 ? parseInt(tp[0], 10) : 0;
                                const m = tp.length >= 2 ? tp[1] : '00';
                                const ap = h >= 12 ? 'PM' : 'AM';
                                h = h % 12 || 12;
                                const time12 = `${h}:${m} ${ap}`;
                                if (parts.length === 3) {
                                  return `${parts[2]}/${parts[1]}/${parts[0]} ${time12}`;
                                }
                                return `${popupData.gatepassDetails.dateIn} ${time12}`;
                              })()
                            : popupData.gatepassDetails?.dateIn || '--'}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="gatepass-popup-utilization">
                    <h5>Actual Times</h5>
                    <div className="gatepass-popup-util-grid">
                      <div className="gatepass-popup-util-item exit">
                        <span className="gatepass-popup-util-label">Actual Exit</span>
                        <span className={`gatepass-popup-util-value ${!popupData.gatepassDetails?.actualExitAt ? 'not-done' : ''}`}>
                          {popupData.gatepassDetails?.actualExitAt ? formatDateTime(popupData.gatepassDetails.actualExitAt) : 'Not Done Yet'}
                        </span>
                      </div>
                      <div className="gatepass-popup-util-item entry">
                        <span className="gatepass-popup-util-label">Actual Entry</span>
                        <span className={`gatepass-popup-util-value ${!popupData.gatepassDetails?.actualEntryAt ? 'not-done' : ''}`}>
                          {popupData.gatepassDetails?.actualEntryAt ? formatDateTime(popupData.gatepassDetails.actualEntryAt) : 'Not Done Yet'}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="gatepass-popup-timeline">
                    <h5>Timeline</h5>
                    <div className="gatepass-popup-timeline-list">
                      <div className="gatepass-popup-timeline-item">
                        <span className="gatepass-popup-timeline-stage">Applied</span>
                        <span className="gatepass-popup-timeline-time">
                          {formatDateTime(popupData.gatepassDetails?.appliedAt) || '--'}
                        </span>
                      </div>
                      {popupData.gatepassDetails?.approvedAt && (
                        <div className={`gatepass-popup-timeline-item ${popupData.gatepassDetails?.status === 'denied' ? 'denied' : 'approved'}`}>
                          <span className="gatepass-popup-timeline-stage">
                            {popupData.gatepassDetails?.status === 'denied' ? 'Denied' : 'Approved'}
                          </span>
                          <span className="gatepass-popup-timeline-time">
                            {formatDateTime(popupData.gatepassDetails.approvedAt)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {popupData.gatepassDetails?.status === 'denied' && (
                    <div className="gatepass-popup-details">
                      <div className="gatepass-popup-detail-item full-width">
                        <span className="gatepass-popup-detail-label">Rejection Reason</span>
                        <span className="gatepass-popup-detail-value">
                          {popupData.gatepassDetails?.rejectionReason || '—'}
                        </span>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Student ID Card Popup */}
      <StudentIdCardPopup
        student={selectedStudent}
        isOpen={showIdCard}
        onClose={() => setShowIdCard(false)}
      />
    </div>
  );
};

// ==================== OUTSTATION GATEPASS VIEWS ====================

const OSRequestsView = ({ onViewDetails }) => {
  const [gatepasses, setGatepasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // ID Card popup state
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showIdCard, setShowIdCard] = useState(false);

  const handleProfileClick = (student, gatepass) => {
    setSelectedStudent({
      ...student,
      name: gatepass.studentName,
      rollnumber: gatepass.rollnumber,
      branch: gatepass.branch || gatepass.department,
      department: gatepass.department,
      contact: gatepass.contact,
      roomNumber: gatepass.roomNumber,
      hostelName: gatepass.hostelName,
      course: gatepass.course,
    });
    setShowIdCard(true);
  };

  const fetchGatepasses = async () => {
    try {
      setError('');
      const res = await getHostelOfficeOSPendingGatepasses();
      setGatepasses(res.data.gatepasses || []);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to fetch gatepasses');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGatepasses();
  }, []);

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    let hours = d.getHours();
    const mins = String(d.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    return `${day}/${month}/${year},  ${hours}:${mins} ${ampm}`;
  };

  const sortedGatepasses = [...gatepasses].sort((a, b) =>
    new Date(a.createdAt) - new Date(b.createdAt)
  );

  return (
    <div className="os-section">
      <h2 className="os-section-title">Pending Outstation Gatepass Requests (Hostel Office)</h2>

      {loading && <div className="os-loading">Loading requests...</div>}
      {error && <div className="os-error">{error}</div>}

      {!loading && gatepasses.length === 0 && (
        <div className="os-empty">No pending outstation gatepass requests</div>
      )}

      <div className="os-cards-grid">
        {sortedGatepasses.map((gp) => (
          <div key={gp._id} className="os-request-card">
            <div
              className="os-card-avatar profile-pic-hover"
              onClick={() => handleProfileClick(gp.student, gp)}
              title="Click to view GoThru ID Card"
            >
              {gp.student?.imageUrl ? (
                <img
                  src={getImageUrl(gp.student.imageUrl)}
                  alt="Student"
                />
              ) : (
                <span style={{ fontSize: '20px', color: '#fff' }}>●</span>
              )}
            </div>
            <div className="os-card-info">
              <div className="os-card-name">{gp.studentName}</div>
              <div className="os-card-details">
                <span>{gp.course}</span>
                <span>{gp.rollnumber}</span>
              </div>
              <div className="os-card-branch">{gp.branch || gp.department}</div>
              <div className="os-card-applied">Applied: {formatDateTime(gp.createdAt)}</div>
            </div>
            <button className="os-view-btn" onClick={() => onViewDetails(gp._id)}>
              View Details
            </button>
          </div>
        ))}
      </div>

      {/* Student ID Card Popup */}
      <StudentIdCardPopup
        student={selectedStudent}
        isOpen={showIdCard}
        onClose={() => setShowIdCard(false)}
      />
    </div>
  );
};

const OSGatepassDetailsView = ({ gatepassId, onBack }) => {
  const [gatepass, setGatepass] = useState(null);
  const [studentHistory, setStudentHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [popupMessage, setPopupMessage] = useState('');
  const [deciding, setDeciding] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ open: false, decision: null });
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showDocPopup, setShowDocPopup] = useState(false);

  // ID Card popup state
  const [showIdCard, setShowIdCard] = useState(false);

  // History gatepass popup state
  const [historyPopup, setHistoryPopup] = useState({ open: false, gatepass: null });

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        setError('');
        const gpRes = await getHostelOfficeOSGatepassDetails(gatepassId);
        setGatepass(gpRes.data.gatepass);

        if (gpRes.data.gatepass?.student?._id) {
          const historyRes = await getHostelOfficeOSStudentHistory(gpRes.data.gatepass.student._id);
          setStudentHistory(historyRes.data.gatepasses || []);
        }
      } catch (err) {
        setError(err?.response?.data?.message || 'Failed to fetch details');
      } finally {
        setLoading(false);
      }
    };
    fetchDetails();
  }, [gatepassId]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
    return dateStr;
  };

  const formatTime12hr = (timeStr) => {
    if (!timeStr) return '';
    const parts = timeStr.split(':');
    if (parts.length < 2) return timeStr;
    let hours = parseInt(parts[0], 10);
    const minutes = parts[1];
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    return `${hours}:${minutes} ${ampm}`;
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    let hours = d.getHours();
    const mins = String(d.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    return `${day}/${month}/${year},  ${hours}:${mins} ${ampm}`;
  };

  const openConfirmModal = (decision) => {
    if (decision === 'rejected') {
      setShowRejectModal(true);
    } else {
      setConfirmModal({ open: true, decision });
    }
  };

  const closeConfirmModal = () => {
    setConfirmModal({ open: false, decision: null });
  };

  const handleRejectConfirm = async () => {
    if (!rejectionReason.trim()) {
      setPopupMessage('Please enter a reason for rejection');
      return;
    }
    setDeciding(true);
    try {
      const res = await decideHostelOfficeOSGatepass({ gatepassId, decision: 'rejected', rejectionReason });
      setShowRejectModal(false);
      setPopupMessage(res.data.message);
      setTimeout(() => onBack(), 1500);
    } catch (err) {
      setPopupMessage(err?.response?.data?.message || 'Failed to process decision');
    } finally {
      setDeciding(false);
    }
  };

  const handleDecision = async (decision) => {
    setDeciding(true);
    try {
      const res = await decideHostelOfficeOSGatepass({ gatepassId, decision });
      closeConfirmModal();
      setPopupMessage(res.data.message);
      setTimeout(() => onBack(), 1500);
    } catch (err) {
      setPopupMessage(err?.response?.data?.message || 'Failed to process decision');
      closeConfirmModal();
    } finally {
      setDeciding(false);
    }
  };

  if (loading) return <div className="os-loading">Loading details...</div>;
  if (error) return <div className="os-error">{error}</div>;
  if (!gatepass) return <div className="os-error">Gatepass not found</div>;

  return (
    <div className="os-details-view">
      <button className="os-back-btn" onClick={onBack}>
        ← Back to Requests
      </button>

      <h2 className="os-section-title">Outstation Gatepass Details</h2>

      <div className="os-details-card">
        {/* Student Profile Section */}
        <div className="os-student-profile-section">
          <div
            className="os-student-photo-large profile-pic-hover"
            onClick={() => setShowIdCard(true)}
            title="Click to view GoThru ID Card"
          >
            {gatepass.student?.imageUrl ? (
              <img
                src={getImageUrl(gatepass.student.imageUrl)}
                alt={gatepass.studentName}
              />
            ) : (
              <div className="os-photo-placeholder">👤</div>
            )}
          </div>
          <div className="os-student-basic-info">
            <h3 className="os-student-name">{gatepass.studentName}</h3>
            <p className="os-student-roll">{gatepass.rollnumber}</p>
            <p className="os-student-branch">{gatepass.course} • {gatepass.department}</p>
          </div>
        </div>

        <div className="os-details-grid">
          <div className="os-detail-item">
            <span className="os-detail-label">Course</span>
            <span className="os-detail-value">{gatepass.course || '--'}</span>
          </div>
          <div className="os-detail-item">
            <span className="os-detail-label">Department</span>
            <span className="os-detail-value">{gatepass.department || '--'}</span>
          </div>
          <div className="os-detail-item">
            <span className="os-detail-label">Room / Hostel</span>
            <span className="os-detail-value">{gatepass.roomNumber} / {gatepass.hostelName || gatepass.student?.hostelName || '--'}</span>
          </div>
          <div className="os-detail-item">
            <span className="os-detail-label">Contact</span>
            <span className="os-detail-value">{gatepass.contact}</span>
          </div>
          <div className="os-detail-item">
            <span className="os-detail-label">Exit Date</span>
            <span className="os-detail-value">{formatDate(gatepass.dateOut)} {formatTime12hr(gatepass.timeOut)}</span>
          </div>
          <div className="os-detail-item">
            <span className="os-detail-label">Return Date</span>
            <span className="os-detail-value">{formatDate(gatepass.dateIn)} {formatTime12hr(gatepass.timeIn)}</span>
          </div>
          <div className="os-detail-item">
            <span className="os-detail-label">Leave Days</span>
            <span className="os-detail-value">{gatepass.leaveDays}</span>
          </div>
          <div className="os-detail-item">
            <span className="os-detail-label">Address During Leave</span>
            <span className="os-detail-value">{gatepass.address}</span>
          </div>
          <div className="os-detail-item">
            <span className="os-detail-label">Nature of Leave</span>
            <span className="os-detail-value">{gatepass.natureOfLeave}</span>
          </div>
          <div className="os-detail-item">
            <span className="os-detail-label">Reason for Leave</span>
            <span className="os-detail-value">{gatepass.reasonOfLeave}</span>
          </div>
          <div className="os-detail-item">
            <span className="os-detail-label">Classes Missed</span>
            <span className="os-detail-value">
              {gatepass.classesMissed === 'yes'
                ? `Yes (${gatepass.missedDays || 0} days)`
                : gatepass.classesMissed === 'no'
                  ? 'No'
                  : 'Not specified'}
            </span>
          </div>
        </div>

        {/* Previous Leaves Taken - from Office Secretary */}
        {gatepass.previousLeavesTaken && (
          <div className="os-detail-full os-previous-leaves-display">
            <span className="os-detail-label">Previous Leaves Taken (Office Secretary Note)</span>
            <span className="os-detail-value">{gatepass.previousLeavesTaken}</span>
          </div>
        )}

        {gatepass.proofFile && (
          <div className="os-detail-full" style={{ marginTop: '16px' }}>
            <span className="os-detail-label">Supporting Document</span>
            <div style={{ marginTop: '8px' }}>
              <button
                className="os-view-doc-btn"
                onClick={() => setShowDocPopup(true)}
              >
                View Document
              </button>
            </div>
          </div>
        )}

        {/* Approval Timeline */}
        <div className="os-approval-timeline">
          <h4>Approval Timeline</h4>
          <div className="os-timeline-item">
            <span className="os-timeline-label">Student Applied</span>
            <span className="os-timeline-value">{formatDateTime(gatepass.createdAt)}</span>
          </div>
          {/* Driven by the stages that actually ran, so every course shows its
              real chain: PhD has Instructor/DPGC/Dean, MBA and MSC have DPGC,
              BTech has DUGC. */}
          {gatepass.stageStatus?.instructor?.decidedAt && (
            <div className="os-timeline-item approved">
              <span className="os-timeline-label">Instructor Approved</span>
              <span className="os-timeline-value">{formatDateTime(gatepass.stageStatus.instructor.decidedAt)}</span>
            </div>
          )}
          {gatepass.stageStatus?.officeSecretary?.decidedAt && (
            <div className="os-timeline-item approved">
              <span className="os-timeline-label">Office Secretary Approved</span>
              <span className="os-timeline-value">{formatDateTime(gatepass.stageStatus.officeSecretary.decidedAt)}</span>
            </div>
          )}
          {gatepass.stageStatus?.dpgc?.decidedAt && (
            <div className="os-timeline-item approved">
              <span className="os-timeline-label">DPGC Approved</span>
              <span className="os-timeline-value">{formatDateTime(gatepass.stageStatus.dpgc.decidedAt)}</span>
            </div>
          )}
          {gatepass.stageStatus?.dugc?.decidedAt && (
            <div className="os-timeline-item approved">
              <span className="os-timeline-label">DUGC Approved</span>
              <span className="os-timeline-value">{formatDateTime(gatepass.stageStatus.dugc.decidedAt)}</span>
            </div>
          )}
          {gatepass.stageStatus?.hod?.decidedAt && (
            <div className="os-timeline-item approved">
              <span className="os-timeline-label">HOD Approved</span>
              <span className="os-timeline-value">{formatDateTime(gatepass.stageStatus.hod.decidedAt)}</span>
            </div>
          )}
          {gatepass.stageStatus?.dean?.decidedAt && (
            <div className="os-timeline-item approved">
              <span className="os-timeline-label">Dean Approved</span>
              <span className="os-timeline-value">{formatDateTime(gatepass.stageStatus.dean.decidedAt)}</span>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        {canDecideOutstationGatepass(gatepass, 'hostelOffice') && <div className="os-action-btns">
          <button
            className="os-approve-btn"
            onClick={() => openConfirmModal('approved')}
            disabled={deciding}
          >
            Approve (Final)
          </button>
        </div>}
      </div>

      <h3 className="os-history-title">Student's Previous Outstation Gatepasses</h3>
      <div className="os-history-list">
        {studentHistory.filter(h => h._id !== gatepassId).length === 0 && (
          <div className="os-empty">No previous outstation gatepasses</div>
        )}
        {studentHistory.filter(h => h._id !== gatepassId).map((h) => (
          <div key={h._id} className={`os-history-item ${getOutstationLifecycleStatus(h)}`}>
            <div className="os-history-header">
              {h.gatePassNo && (
                <span
                  className="os-history-gatepass-tag"
                  onClick={() => setHistoryPopup({ open: true, gatepass: h })}
                  title="Click to view details"
                >
                  {h.gatePassNo}
                </span>
              )}
              <span className="os-history-reason">{h.reasonOfLeave}</span>
              <span className={`os-status-badge ${getOutstationLifecycleStatus(h)}`}>
                {getGatepassStatusLabel(getOutstationLifecycleStatus(h))}
              </span>
            </div>
            <div className="os-history-dates">
              Out: {formatDate(h.dateOut)} → In: {formatDate(h.dateIn)}
            </div>
          </div>
        ))}
      </div>

      {/* History Gatepass Details Popup */}
      {historyPopup.open && historyPopup.gatepass && (
        <div className="confirm-modal-overlay" onClick={() => setHistoryPopup({ open: false, gatepass: null })}>
          <div className="os-history-popup" onClick={(e) => e.stopPropagation()}>
            <button className="confirm-modal-close" onClick={() => setHistoryPopup({ open: false, gatepass: null })}>×</button>
            <div className="os-history-popup-header">
              <span className="os-history-popup-tag">{historyPopup.gatepass.gatePassNo}</span>
              <span className={`os-status-badge ${getOutstationLifecycleStatus(historyPopup.gatepass)}`}>
                {getGatepassStatusLabel(getOutstationLifecycleStatus(historyPopup.gatepass))}
              </span>
            </div>
            <div className="os-history-popup-body">
              <div className="os-history-popup-row">
                <label>Reason of Leave:</label>
                <span>{historyPopup.gatepass.reasonOfLeave}</span>
              </div>
              <div className="os-history-popup-row">
                <label>Address:</label>
                <span>{historyPopup.gatepass.address || '--'}</span>
              </div>
              <div className="os-history-popup-row">
                <label>Date Out:</label>
                <span>{formatDate(historyPopup.gatepass.dateOut)}</span>
              </div>
              <div className="os-history-popup-row">
                <label>Date In:</label>
                <span>{formatDate(historyPopup.gatepass.dateIn)}</span>
              </div>
              {historyPopup.gatepass.classesMissed && (
                <div className="os-history-popup-row">
                  <label>Classes Missed:</label>
                  <span>{historyPopup.gatepass.classesMissed === 'yes' ? `Yes (${historyPopup.gatepass.missedDays || 0} days)` : 'No'}</span>
                </div>
              )}
              <div className="os-history-popup-row">
                <label>Applied On:</label>
                <span>{new Date(historyPopup.gatepass.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={confirmModal.open}
        onClose={closeConfirmModal}
        onConfirm={() => handleDecision(confirmModal.decision)}
        decision={confirmModal.decision}
        studentName={gatepass?.studentName}
        rollNumber={gatepass?.rollnumber}
        reasonOfLeave={gatepass?.reasonOfLeave}
        isProcessing={deciding}
      />

      {/* Rejection Modal */}
      {showRejectModal && (
        <div className="doc-popup-overlay" onClick={() => setShowRejectModal(false)}>
          <div className="doc-popup-content" style={{ maxWidth: '500px', padding: '24px' }} onClick={(e) => e.stopPropagation()}>
            <button className="doc-popup-close" onClick={() => setShowRejectModal(false)}>×</button>
            <h3 style={{ marginBottom: '16px' }}>Reject Gatepass</h3>
            <p style={{ marginBottom: '12px' }}>Are you sure you want to reject this gatepass?</p>
            <textarea
              className="rejection-reason-input"
              placeholder="Enter reason for rejection..."
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              rows={4}
              style={{ width: '100%', marginBottom: '16px', padding: '10px', borderRadius: '6px', border: '1px solid var(--color-border)', resize: 'none' }}
            />
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowRejectModal(false)}
                style={{ padding: '10px 20px', borderRadius: '6px', border: '1px solid var(--color-border)', background: 'white', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={handleRejectConfirm}
                disabled={deciding}
                style={{ padding: '10px 20px', borderRadius: '6px', border: 'none', background: '#dc3545', color: 'white', cursor: 'pointer', opacity: deciding ? 0.6 : 1 }}
              >
                {deciding ? 'Processing...' : 'Confirm Rejection'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Document Popup */}
      {showDocPopup && gatepass.proofFile && (
        <div className="doc-popup-overlay" onClick={() => setShowDocPopup(false)}>
          <div className="doc-popup-content" onClick={(e) => e.stopPropagation()}>
            <button className="doc-popup-close" onClick={() => setShowDocPopup(false)}>×</button>
            {gatepass.proofFile.endsWith('.pdf') ? (
              <iframe
                className="doc-popup-iframe"
                src={`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000'}${gatepass.proofFile}`}
                title="Document Preview"
              />
            ) : (
              <img
                className="doc-popup-image"
                src={`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000'}${gatepass.proofFile}`}
                alt="Proof Document"
              />
            )}
          </div>
        </div>
      )}

      {/* Student ID Card Popup */}
      <StudentIdCardPopup
        student={gatepass ? {
          ...gatepass.student,
          name: gatepass.studentName,
          rollnumber: gatepass.rollnumber,
          branch: gatepass.branch || gatepass.department,
          department: gatepass.department,
          contact: gatepass.contact,
          roomNumber: gatepass.roomNumber,
          hostelName: gatepass.hostelName,
        } : null}
        isOpen={showIdCard}
        onClose={() => setShowIdCard(false)}
      />

      <PopupBox message={popupMessage} onClose={() => setPopupMessage('')} />
    </div>
  );
};

const OSHistoryView = () => {
  const [gatepasses, setGatepasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  // Popup state
  const [showPopup, setShowPopup] = useState(false);
  const [popupData, setPopupData] = useState(null);
  const [popupLoading, setPopupLoading] = useState(false);

  // ID Card popup state
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showIdCard, setShowIdCard] = useState(false);

  const handleProfileClick = (e, student, gp) => {
    e.stopPropagation(); // Prevent row click
    setSelectedStudent({
      ...student,
      name: gp.studentName,
      rollnumber: gp.rollnumber,
      branch: gp.branch || gp.department,
      department: gp.department,
      contact: gp.contact,
      roomNumber: gp.roomNumber,
      hostelName: gp.hostelName,
      course: gp.course,
    });
    setShowIdCard(true);
  };

  const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

  const fetchHistory = async (searchQuery = '') => {
    try {
      setError('');
      setLoading(true);
      const res = await getHostelOfficeOSGatepassHistory(searchQuery);
      setGatepasses(res.data.gatepasses || []);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to fetch history');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchHistory(search);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
    return dateStr;
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    const datePart = d.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
    const timePart = d.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
    return `${datePart}, ${timePart}`;
  };

  const handleRowClick = async (gp) => {
    if (!gp._id) return;

    setShowPopup(true);
    setPopupLoading(true);
    setPopupData(null);

    try {
      const res = await getHostelOfficeOSGatepassDetails(gp._id);
      const gatepass = res.data.gatepass;
      setPopupData({
        student: {
          ...gatepass.student,
          name: gatepass.studentName || gatepass.student?.name,
          rollnumber: gatepass.rollnumber || gatepass.student?.rollnumber,
          course: gatepass.course || gatepass.student?.course,
          branch: gatepass.branch || gatepass.student?.branch || gatepass.department || gatepass.student?.department,
          department: gatepass.department || gatepass.student?.department,
          roomNumber: gatepass.roomNumber || gatepass.student?.roomNumber,
          hostelName: gatepass.hostelName || gatepass.student?.hostelName,
          contact: gatepass.contact || gatepass.student?.contactNumber,
          contactNumber: gatepass.contact || gatepass.student?.contactNumber,
          presence: gatepass.student?.presence,
        },
        gatePassNo: gatepass.gatePassNo,
        gatepassDetails: {
          natureOfLeave: gatepass.natureOfLeave,
          leaveDays: gatepass.leaveDays,
          reasonOfLeave: gatepass.reasonOfLeave,
          address: gatepass.address,
          status: gatepass.currentStage,
          finalStatus: gatepass.finalStatus,
          utilizationStatus: gatepass.utilizationStatus,
          actualExitAt: gatepass.actualExitAt,
          actualEntryAt: gatepass.actualEntryAt,
          appliedAt: gatepass.createdAt,
          approvedAt: gatepass.stageStatus?.hostelOffice?.decidedAt,
          course: gatepass.course,
          stageStatus: gatepass.stageStatus,
          dateOut: gatepass.dateOut,
          timeOut: gatepass.timeOut,
          dateIn: gatepass.dateIn,
          timeIn: gatepass.timeIn,
          instructorNote: gatepass.instructorNote,
          officeSecretaryNote: gatepass.officeSecretaryNote,
          dugcNote: gatepass.dugcNote,
          dpgcNote: gatepass.dpgcNote,
          hodNote: gatepass.hodNote,
          deanNote: gatepass.deanNote,
          previousLeavesTaken: gatepass.previousLeavesTaken,
          classesMissed: gatepass.classesMissed,
          missedDays: gatepass.missedDays,
          rejectionReason: gatepass.rejectionReason,
        }
      });
    } catch (err) {
      setPopupData({ error: err.response?.data?.message || 'Failed to load gatepass details' });
    } finally {
      setPopupLoading(false);
    }
  };

  const closePopup = () => {
    setShowPopup(false);
    setPopupData(null);
  };

  return (
    <div className="os-section">
      <div className="os-history-header-row">
        <h2 className="os-section-title">Outstation Gatepass History (Hostel Office)</h2>
        <form onSubmit={handleSearch} className="os-search-form">
          <input
            type="text"
            placeholder="Search by name or roll number..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="os-search-input"
          />
          <button type="submit" className="os-search-btn">🔍</button>
        </form>
      </div>

      {loading && <div className="os-loading">Loading history...</div>}
      {error && <div className="os-error">{error}</div>}

      {!loading && gatepasses.length === 0 && (
        <div className="os-empty">No history found</div>
      )}

      <div className="os-history-table">
        {gatepasses.map((gp) => {
          const hasGatepassNo = !!gp.gatePassNo;
          const isClickable = !!gp._id;
          const status = getApproverHistoryStatus(gp, 'hostelOffice');

          return (
            <div
              key={gp._id}
              className={`os-history-row-enhanced ${status} ${isClickable ? 'os-history-row-clickable' : ''}`}
              onClick={() => isClickable && handleRowClick(gp)}
              role={isClickable ? 'button' : undefined}
              tabIndex={isClickable ? 0 : undefined}
            >
              {/* Student Avatar */}
              <div
                className="os-history-avatar profile-pic-hover"
                onClick={(e) => handleProfileClick(e, gp.student, gp)}
                title="Click to view GoThru ID Card"
              >
                {gp.student?.imageUrl ? (
                  <img src={`${API_BASE}${gp.student.imageUrl}`} alt="" />
                ) : (
                  <div className="os-history-avatar-placeholder">
                    {gp.studentName?.charAt(0) || '?'}
                  </div>
                )}
              </div>

              {/* Student Info */}
              <div className="os-history-student-info">
                <div className="os-history-name">{gp.studentName}</div>
                <div className="os-history-meta">{gp.rollnumber} • {gp.course} • {gp.department}</div>
              </div>

              {/* Gatepass Number */}
              {hasGatepassNo ? (
                <span className="os-history-gp-number">{gp.gatePassNo}</span>
              ) : status !== 'rejected' ? (
                <span className="os-history-gp-pending">Yet to be Approved</span>
              ) : null}

              {/* Leave Info */}
              <div className="os-history-leave-enhanced">
                <span>{formatDate(gp.dateOut)} - {formatDate(gp.dateIn)}</span>
                <span className="os-history-reason-text">{gp.reasonOfLeave}</span>
              </div>

              {/* Status Badge */}
              <div className={`os-status-badge ${status}`}>
                {getGatepassStatusLabel(status)}
              </div>
            </div>
          );
        })}
      </div>

      {/* Gatepass Detail Popup */}
      {showPopup && (
        <div className="gatepass-popup-overlay" onClick={closePopup}>
          <div className="gatepass-popup-content" onClick={(e) => e.stopPropagation()}>
            <div className="gatepass-popup-header">
              <h3>Outstation Gatepass Details</h3>
              <button className="gatepass-popup-close" onClick={closePopup}>×</button>
            </div>
            <div className="gatepass-popup-body">
              {popupLoading && <div className="os-loading">Loading gatepass details...</div>}
              {!popupLoading && popupData?.error && (
                <div className="os-error">{popupData.error}</div>
              )}
              {!popupLoading && popupData && !popupData.error && (
                <>
                  <div className="gatepass-popup-student">
                    <div
                      className="gatepass-popup-avatar profile-pic-hover"
                      onClick={() => {
                        setSelectedStudent(popupData.student);
                        setShowIdCard(true);
                      }}
                      title="Click to view GoThru ID Card"
                    >
                      {popupData.student?.imageUrl ? (
                        <img src={`${API_BASE}${popupData.student.imageUrl}`} alt="" />
                      ) : (
                        <div className="gatepass-popup-avatar-placeholder">
                          {popupData.student?.name?.charAt(0) || '?'}
                        </div>
                      )}
                    </div>
                    <div className="gatepass-popup-student-info">
                      <h4>{popupData.student?.name}</h4>
                      <p>{popupData.student?.rollnumber} • {popupData.student?.department}</p>
                    </div>
                  </div>

                  <div className="gatepass-popup-badges">
                    <span className="gatepass-popup-type-badge outstation">OUTSTATION</span>
                    <span className="gatepass-popup-number-badge">{popupData.gatePassNo}</span>
                    <span className={`gatepass-popup-status-badge ${getOutstationLifecycleStatus(popupData.gatepassDetails)}`}>
                      {getGatepassStatusLabel(getOutstationLifecycleStatus(popupData.gatepassDetails))}
                    </span>
                  </div>

                  <div className="gatepass-popup-details">
                    <div className="gatepass-popup-detail-item">
                      <span className="gatepass-popup-detail-label">Nature of Leave</span>
                      <span className="gatepass-popup-detail-value">{popupData.gatepassDetails?.natureOfLeave || '--'}</span>
                    </div>
                    <div className="gatepass-popup-detail-item">
                      <span className="gatepass-popup-detail-label">Leave Days</span>
                      <span className="gatepass-popup-detail-value">{popupData.gatepassDetails?.leaveDays || '--'}</span>
                    </div>
                    <div className="gatepass-popup-detail-item full-width">
                      <span className="gatepass-popup-detail-label">Reason</span>
                      <span className="gatepass-popup-detail-value">{popupData.gatepassDetails?.reasonOfLeave || '--'}</span>
                    </div>
                    <div className="gatepass-popup-detail-item full-width">
                      <span className="gatepass-popup-detail-label">Address During Leave</span>
                      <span className="gatepass-popup-detail-value">{popupData.gatepassDetails?.address || '--'}</span>
                    </div>
                  </div>

                  <div className="gatepass-popup-utilization">
                    <h5>Scheduled Times</h5>
                    <div className="gatepass-popup-util-grid">
                      <div className="gatepass-popup-util-item exit">
                        <span className="gatepass-popup-util-label">Scheduled Exit</span>
                        <span className="gatepass-popup-util-value scheduled">
                          {formatScheduledDateTime(popupData.gatepassDetails?.dateOut, popupData.gatepassDetails?.timeOut)}
                        </span>
                      </div>
                      <div className="gatepass-popup-util-item entry">
                        <span className="gatepass-popup-util-label">Scheduled Entry</span>
                        <span className="gatepass-popup-util-value scheduled">
                          {formatScheduledDateTime(popupData.gatepassDetails?.dateIn, popupData.gatepassDetails?.timeIn)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="gatepass-popup-utilization">
                    <h5>Utilization Status</h5>
                    <div className="gatepass-popup-util-grid">
                      <div className="gatepass-popup-util-item exit">
                        <span className="gatepass-popup-util-label">Actual Exit</span>
                        <span className={`gatepass-popup-util-value ${!popupData.gatepassDetails?.actualExitAt ? 'not-done' : ''}`}>
                          {popupData.gatepassDetails?.actualExitAt ? formatDateTime(popupData.gatepassDetails.actualExitAt) : 'Not Done Yet'}
                        </span>
                      </div>
                      <div className="gatepass-popup-util-item entry">
                        <span className="gatepass-popup-util-label">Actual Entry</span>
                        <span className={`gatepass-popup-util-value ${!popupData.gatepassDetails?.actualEntryAt ? 'not-done' : ''}`}>
                          {popupData.gatepassDetails?.actualEntryAt ? formatDateTime(popupData.gatepassDetails.actualEntryAt) : 'Not Done Yet'}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="gatepass-popup-timeline">
                    <h5>Approval Timeline</h5>
                    <div className="gatepass-popup-timeline-list">
                      <div className="gatepass-popup-timeline-item">
                        <span className="gatepass-popup-timeline-stage">Applied</span>
                        <span className="gatepass-popup-timeline-time">
                          {formatDateTime(popupData.gatepassDetails?.appliedAt) || '--'}
                        </span>
                      </div>
                      {popupData.gatepassDetails?.approvedAt && (
                        <div className="gatepass-popup-timeline-item approved">
                          <span className="gatepass-popup-timeline-stage">Final Approval</span>
                          <span className="gatepass-popup-timeline-time">
                            {formatDateTime(popupData.gatepassDetails.approvedAt)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  <GatepassExtraInfo details={popupData.gatepassDetails} />
                  <GatepassStageNotes details={popupData.gatepassDetails} />
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Student ID Card Popup */}
      <StudentIdCardPopup
        student={selectedStudent}
        isOpen={showIdCard}
        onClose={() => setShowIdCard(false)}
      />
    </div>
  );
};

const LogRegisterView = () => {
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState(''); // Filter for late/outside students
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  // Gatepass detail popup (triggered from gate pass number click)
  const [showPopup, setShowPopup] = useState(false);
  const [popupLoading, setPopupLoading] = useState(false);
  const [popupType, setPopupType] = useState(null); // 'LOCAL' | 'OUTSTATION'
  const [popupData, setPopupData] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showIdCard, setShowIdCard] = useState(false);

  const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return '';
    const datePart = d.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
    const timePart = d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
    return `${datePart}, ${timePart}`;
  };

  const handleGatePassClick = async (gatePassNo) => {
    if (!gatePassNo || gatePassNo === '--') return;
    setShowPopup(true);
    setPopupLoading(true);
    setPopupData(null);
    setPopupType(null);
    try {
      const res = await getGatepassByNumber(gatePassNo);
      const type = res.data.type;
      const gp = res.data.gatepass;
      setPopupType(type);
      if (type === 'LOCAL') {
        setPopupData({
          student: {
            ...gp.student,
            name: gp.studentName || gp.student?.name,
            rollnumber: gp.rollnumber || gp.student?.rollnumber,
            course: gp.course || gp.student?.course,
            branch: gp.branch || gp.student?.branch || gp.department || gp.student?.department,
            department: gp.department || gp.student?.department,
            roomNumber: gp.roomNumber || gp.student?.roomNumber,
            hostelName: gp.hostelName || gp.student?.hostelName,
            contact: gp.contact || gp.student?.contactNumber,
            contactNumber: gp.contact || gp.student?.contactNumber,
            presence: gp.student?.presence,
          },
          gatePassNo: gp.gatePassNo,
          gatepassDetails: {
            purpose: gp.purpose,
            place: gp.place,
            status: gp.status,
            dateOut: gp.dateOut,
            dateIn: gp.dateIn,
            timeOut: gp.timeOut,
            timeIn: gp.timeIn,
            actualExitAt: gp.actualExitAt,
            actualEntryAt: gp.actualEntryAt,
            appliedAt: gp.createdAt,
            approvedAt: gp.decidedAt,
            rejectionReason: gp.rejectionReason,
          },
        });
      } else {
        setPopupData({
          student: {
            ...gp.student,
            name: gp.studentName || gp.student?.name,
            rollnumber: gp.rollnumber || gp.student?.rollnumber,
            course: gp.course || gp.student?.course,
            branch: gp.branch || gp.student?.branch || gp.department || gp.student?.department,
            department: gp.department || gp.student?.department,
            roomNumber: gp.roomNumber || gp.student?.roomNumber,
            hostelName: gp.hostelName || gp.student?.hostelName,
            contact: gp.contact || gp.student?.contactNumber,
            contactNumber: gp.contact || gp.student?.contactNumber,
            presence: gp.student?.presence,
          },
          gatePassNo: gp.gatePassNo,
          gatepassDetails: {
            natureOfLeave: gp.natureOfLeave,
            leaveDays: gp.leaveDays,
            reasonOfLeave: gp.reasonOfLeave,
            address: gp.address,
            status: gp.currentStage,
            finalStatus: gp.finalStatus,
            utilizationStatus: gp.utilizationStatus,
            actualExitAt: gp.actualExitAt,
            actualEntryAt: gp.actualEntryAt,
            appliedAt: gp.createdAt,
            approvedAt: gp.stageStatus?.hostelOffice?.decidedAt,
            course: gp.course,
            stageStatus: gp.stageStatus,
            dateOut: gp.dateOut,
            timeOut: gp.timeOut,
            dateIn: gp.dateIn,
            timeIn: gp.timeIn,
            instructorNote: gp.instructorNote,
            officeSecretaryNote: gp.officeSecretaryNote,
            dugcNote: gp.dugcNote,
            dpgcNote: gp.dpgcNote,
            hodNote: gp.hodNote,
            deanNote: gp.deanNote,
            previousLeavesTaken: gp.previousLeavesTaken,
            classesMissed: gp.classesMissed,
            missedDays: gp.missedDays,
            rejectionReason: gp.rejectionReason,
          },
        });
      }
    } catch (err) {
      setPopupData({ error: err.response?.data?.message || 'Failed to load gatepass details' });
    } finally {
      setPopupLoading(false);
    }
  };

  const closePopup = () => {
    setShowPopup(false);
    setPopupData(null);
    setPopupType(null);
  };

  // Fetch logs when date, search, or filter changes
  useEffect(() => {
    const fetchLogs = async () => {
      setLoading(true);
      try {
        const res = await getEntryExitLogs(date, search, filter || undefined);
        setLogs(res.data.logs || []);
      } catch (err) {
        console.error("Failed to fetch logs", err);
      } finally {
        setLoading(false);
      }
    };

    const timeoutId = setTimeout(() => {
      fetchLogs();
    }, 300); // Debounce search

    return () => clearTimeout(timeoutId);
  }, [date, search, filter]);

  const formatLogTime = (dateStr) => {
    if (!dateStr) return '--';
    const d = new Date(dateStr);
    return d.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    }).toUpperCase();
  };

  // Helper to format date from a datetime string
  const formatLogDate = (dateStr) => {
    if (!dateStr) return '--';
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return '--';
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = String(d.getFullYear()).slice(-2);
    return `${day}/${month}/${year}`;
  };

  // Helper to determine row styling based on flags
  const getRowStyle = (log) => {
    const flags = log.flags || {};

    // Red background for late entries (after 8PM without gatepass OR after gatepass time)
    if (flags.lateAfter8PM || flags.lateGatepass) {
      return { backgroundColor: '#fee2e2' }; // Light red
    }

    // Yellow background for students currently outside
    if (flags.outside) {
      return { backgroundColor: '#fef3c7' }; // Light yellow/amber
    }

    return {};
  };

  const handleDownloadExcel = () => {
    if (!logs.length) return;

    const headers = ["Sr No", "Name", "Roll No", "Room No", "Contact", "Place", "Purpose", "Gate Pass", "Time Out", "Exit Date", "Time In", "Entry Date"];

    const rows = logs.map(log => {
      const srNo = log.srNo || '';
      const name = log.name || '';
      const rollNo = log.rollNo || '';
      const roomNo = log.roomNo || '';
      // Use Excel formula format to force text (no visible prefix)
      const contact = log.contact ? `="${log.contact}"` : '';
      const place = log.place || '';
      const purpose = log.purpose || '';
      const gatePass = log.gatePass || '--';
      const timeOut = formatLogTime(log.timeOut);
      const exitDate = formatLogDate(log.timeOut);
      const timeIn = formatLogTime(log.timeIn);
      const entryDate = formatLogDate(log.timeIn);

      // Escape quotes properly for CSV
      const escapeCSV = (val) => {
        const str = String(val);
        if (str.includes(',') || str.includes('"') || str.includes('\n')) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      };

      return [srNo, name, rollNo, roomNo, contact, place, purpose, gatePass, timeOut, exitDate, timeIn, entryDate]
        .map(escapeCSV)
        .join(',');
    });

    // Add BOM for Excel to recognize UTF-8 encoding
    const BOM = '\uFEFF';
    const csvContent = BOM + [headers.join(','), ...rows].join('\r\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `EntryExitLogs_${date || 'all'}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="os-section" style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', maxWidth: '100%' }}>
      {/* Header with Title and Filters */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap', gap: '8px', flexShrink: 0 }}>
        <h2 className="os-section-title" style={{ margin: 0 }}>Entry-Exit Log Register</h2>
        <div style={{ display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Date Filter */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label style={{ fontWeight: '500', fontSize: '14px', color: '#666' }}>DATE</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              max={new Date().toISOString().split('T')[0]}
              style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px' }}
            />
            {date && (
              <button
                onClick={() => setDate('')}
                style={{ padding: '8px 12px', background: '#f3f4f6', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}
              >
                All
              </button>
            )}
          </div>
          {/* Search Filter */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label style={{ fontWeight: '500', fontSize: '14px', color: '#666' }}>SEARCH</label>
            <input
              type="text"
              placeholder="Name or Roll No..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', minWidth: '180px' }}
            />
          </div>
          {/* Status Filter */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label style={{ fontWeight: '500', fontSize: '14px', color: '#666' }}>FILTER</label>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', minWidth: '180px' }}
            >
              <option value="">All Entries</option>
              <option value="outside">🔴 Outside (Currently)</option>
              <option value="lateAfter8PM">⚠️ Late After 8 PM</option>
              <option value="lateLocalGatepass">⚠️ Late (Local Gatepass)</option>
              <option value="lateOutstationGatepass">⚠️ Late (Outstation Gatepass)</option>
            </select>
          </div>
          {/* Download Button */}
          <button
            onClick={handleDownloadExcel}
            disabled={logs.length === 0}
            style={{
              background: logs.length === 0 ? '#9ca3af' : '#10b981',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '6px',
              cursor: logs.length === 0 ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            📥 Download Excel
          </button>
        </div>
      </div>

      {/* Table */}
      <div style={{ background: 'white', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <div style={{ overflowY: 'auto', overflowX: 'hidden', flex: 1 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px', tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: '38px' }} />
              <col style={{ width: '13%' }} />
              <col style={{ width: '8%' }} />
              <col style={{ width: '5%' }} />
              <col style={{ width: '9%' }} />
              <col style={{ width: '9.5%' }} />
              <col style={{ width: '8.5%' }} />
              <col style={{ width: '7.5%' }} />
              <col style={{ width: '7.5%' }} />
              <col style={{ width: '7.5%' }} />
              <col style={{ width: '7.5%' }} />
              <col style={{ width: '7.5%' }} />
            </colgroup>
            <thead>
              <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #e5e7eb' }}>
                <th style={{ padding: '10px 6px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '10px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>SR</th>
                <th style={{ padding: '10px 6px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '10px' }}>NAME</th>
                <th style={{ padding: '10px 6px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '10px' }}>ROLL NO.</th>
                <th style={{ padding: '10px 6px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '10px' }}>ROOM</th>
                <th style={{ padding: '10px 6px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '10px' }}>CONTACT</th>
                <th style={{ padding: '10px 6px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '10px' }}>PLACE</th>
                <th style={{ padding: '10px 6px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '10px' }}>PURPOSE</th>
                <th style={{ padding: '10px 6px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '10px' }}>GATE PASS</th>
                <th style={{ padding: '10px 6px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '10px' }}>TIME OUT</th>
                <th style={{ padding: '10px 6px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '10px' }}>EXIT DATE</th>
                <th style={{ padding: '10px 6px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '10px' }}>TIME IN</th>
                <th style={{ padding: '10px 6px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '10px' }}>ENTRY DATE</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={12} style={{ padding: '40px', textAlign: 'center', color: '#666' }}>Loading...</td>
                </tr>
              )}
              {!loading && logs.length === 0 && (
                <tr>
                  <td colSpan={12} style={{ padding: '40px', textAlign: 'center', color: '#666' }}>No records found</td>
                </tr>
              )}
              {!loading && logs.map((log) => {
                const rowStyle = getRowStyle(log);
                return (
                  <tr key={log._id} style={{ borderBottom: '1px solid #e5e7eb', ...rowStyle }}>
                    <td style={{ padding: '8px 6px', color: '#ef4444', fontWeight: '500', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{log.srNo}</td>
                    <td style={{ padding: '8px 6px', color: '#111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{log.name}</td>
                    <td style={{ padding: '8px 6px', color: '#111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{log.rollNo}</td>
                    <td style={{ padding: '8px 6px', color: '#111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{log.roomNo}</td>
                    <td style={{ padding: '8px 6px', color: '#111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{log.contact}</td>
                    <td style={{ padding: '8px 6px', color: '#2563eb', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{log.place}</td>
                    <td style={{ padding: '8px 6px', color: '#059669', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{log.purpose}</td>
                    <td style={{ padding: '8px 6px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {log.gatePass && log.gatePass !== '--' ? (
                        <button
                          onClick={() => handleGatePassClick(log.gatePass)}
                          title="View full gatepass details"
                          style={{
                            background: '#eef2ff',
                            color: '#1d4ed8',
                            border: '1px solid #c7d2fe',
                            padding: '3px 8px',
                            borderRadius: '5px',
                            fontSize: '11px',
                            fontWeight: 600,
                            cursor: 'pointer',
                            fontFamily: 'inherit',
                          }}
                        >
                          {log.gatePass}
                        </button>
                      ) : (
                        <span style={{ color: '#666' }}>--</span>
                      )}
                    </td>
                    <td style={{ padding: '8px 6px', color: '#111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{formatLogTime(log.timeOut)}</td>
                    <td style={{ padding: '8px 6px', color: '#111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{formatLogDate(log.timeOut)}</td>
                    <td style={{ padding: '8px 6px', color: log.timeIn ? '#059669' : '#999', fontWeight: log.timeIn ? '500' : 'normal', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {formatLogTime(log.timeIn)}
                    </td>
                    <td style={{ padding: '8px 6px', color: log.timeIn ? '#059669' : '#999', fontWeight: log.timeIn ? '500' : 'normal', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {formatLogDate(log.timeIn)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Gatepass Detail Popup (from Log Register click) */}
      {showPopup && popupType === 'LOCAL' && (
        <div className="gatepass-popup-overlay" onClick={closePopup}>
          <div className="gatepass-popup-content" onClick={(e) => e.stopPropagation()}>
            <div className="gatepass-popup-header">
              <h3>Local Gatepass Details</h3>
              <button className="gatepass-popup-close" onClick={closePopup}>×</button>
            </div>
            <div className="gatepass-popup-body">
              {popupLoading && <div className="os-loading">Loading gatepass details...</div>}
              {!popupLoading && popupData?.error && (
                <div className="os-error">{popupData.error}</div>
              )}
              {!popupLoading && popupData && !popupData.error && (
                <>
                  <div className="gatepass-popup-student">
                    <div
                      className="gatepass-popup-avatar profile-pic-hover"
                      onClick={() => { setSelectedStudent(popupData.student); setShowIdCard(true); }}
                      title="Click to view GoThru ID Card"
                    >
                      {popupData.student?.imageUrl ? (
                        <img src={`${API_BASE}${popupData.student.imageUrl}`} alt="" />
                      ) : (
                        <div className="gatepass-popup-avatar-placeholder">
                          {popupData.student?.name?.charAt(0) || '?'}
                        </div>
                      )}
                    </div>
                    <div className="gatepass-popup-student-info">
                      <h4>{popupData.student?.name}</h4>
                      <p>{popupData.student?.rollnumber} • {popupData.student?.department}</p>
                    </div>
                  </div>

                  <div className="gatepass-popup-badges">
                    <span className="gatepass-popup-type-badge local">LOCAL</span>
                    <span className="gatepass-popup-number-badge">{popupData.gatePassNo}</span>
                    <span className={`gatepass-popup-status-badge ${popupData.gatepassDetails?.status}`}>
                      {popupData.gatepassDetails?.status || 'Pending'}
                    </span>
                  </div>

                  <div className="gatepass-popup-details">
                    <div className="gatepass-popup-detail-item">
                      <span className="gatepass-popup-detail-label">Purpose</span>
                      <span className="gatepass-popup-detail-value">{popupData.gatepassDetails?.purpose || '--'}</span>
                    </div>
                    <div className="gatepass-popup-detail-item">
                      <span className="gatepass-popup-detail-label">Place</span>
                      <span className="gatepass-popup-detail-value">{popupData.gatepassDetails?.place || '--'}</span>
                    </div>
                  </div>

                  <div className="gatepass-popup-utilization">
                    <h5>Scheduled Times</h5>
                    <div className="gatepass-popup-util-grid">
                      <div className="gatepass-popup-util-item exit">
                        <span className="gatepass-popup-util-label">Scheduled Exit</span>
                        <span className="gatepass-popup-util-value scheduled">
                          {formatScheduledDateTime(popupData.gatepassDetails?.dateOut, popupData.gatepassDetails?.timeOut)}
                        </span>
                      </div>
                      <div className="gatepass-popup-util-item entry">
                        <span className="gatepass-popup-util-label">Scheduled Entry</span>
                        <span className="gatepass-popup-util-value scheduled">
                          {formatScheduledDateTime(popupData.gatepassDetails?.dateIn, popupData.gatepassDetails?.timeIn)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="gatepass-popup-utilization">
                    <h5>Actual Times</h5>
                    <div className="gatepass-popup-util-grid">
                      <div className="gatepass-popup-util-item exit">
                        <span className="gatepass-popup-util-label">Actual Exit</span>
                        <span className={`gatepass-popup-util-value ${!popupData.gatepassDetails?.actualExitAt ? 'not-done' : ''}`}>
                          {popupData.gatepassDetails?.actualExitAt ? formatDateTime(popupData.gatepassDetails.actualExitAt) : 'Not Done Yet'}
                        </span>
                      </div>
                      <div className="gatepass-popup-util-item entry">
                        <span className="gatepass-popup-util-label">Actual Entry</span>
                        <span className={`gatepass-popup-util-value ${!popupData.gatepassDetails?.actualEntryAt ? 'not-done' : ''}`}>
                          {popupData.gatepassDetails?.actualEntryAt ? formatDateTime(popupData.gatepassDetails.actualEntryAt) : 'Not Done Yet'}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="gatepass-popup-timeline">
                    <h5>Timeline</h5>
                    <div className="gatepass-popup-timeline-list">
                      <div className="gatepass-popup-timeline-item">
                        <span className="gatepass-popup-timeline-stage">Applied</span>
                        <span className="gatepass-popup-timeline-time">
                          {formatDateTime(popupData.gatepassDetails?.appliedAt) || '--'}
                        </span>
                      </div>
                      {popupData.gatepassDetails?.approvedAt && (
                        <div className={`gatepass-popup-timeline-item ${popupData.gatepassDetails?.status === 'denied' ? 'denied' : 'approved'}`}>
                          <span className="gatepass-popup-timeline-stage">
                            {popupData.gatepassDetails?.status === 'denied' ? 'Denied' : 'Approved'}
                          </span>
                          <span className="gatepass-popup-timeline-time">
                            {formatDateTime(popupData.gatepassDetails.approvedAt)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {popupData.gatepassDetails?.status === 'denied' && (
                    <div className="gatepass-popup-details">
                      <div className="gatepass-popup-detail-item full-width">
                        <span className="gatepass-popup-detail-label">Rejection Reason</span>
                        <span className="gatepass-popup-detail-value">
                          {popupData.gatepassDetails?.rejectionReason || '—'}
                        </span>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {showPopup && popupType === 'OUTSTATION' && (
        <div className="gatepass-popup-overlay" onClick={closePopup}>
          <div className="gatepass-popup-content" onClick={(e) => e.stopPropagation()}>
            <div className="gatepass-popup-header">
              <h3>Outstation Gatepass Details</h3>
              <button className="gatepass-popup-close" onClick={closePopup}>×</button>
            </div>
            <div className="gatepass-popup-body">
              {popupLoading && <div className="os-loading">Loading gatepass details...</div>}
              {!popupLoading && popupData?.error && (
                <div className="os-error">{popupData.error}</div>
              )}
              {!popupLoading && popupData && !popupData.error && (
                <>
                  <div className="gatepass-popup-student">
                    <div
                      className="gatepass-popup-avatar profile-pic-hover"
                      onClick={() => { setSelectedStudent(popupData.student); setShowIdCard(true); }}
                      title="Click to view GoThru ID Card"
                    >
                      {popupData.student?.imageUrl ? (
                        <img src={`${API_BASE}${popupData.student.imageUrl}`} alt="" />
                      ) : (
                        <div className="gatepass-popup-avatar-placeholder">
                          {popupData.student?.name?.charAt(0) || '?'}
                        </div>
                      )}
                    </div>
                    <div className="gatepass-popup-student-info">
                      <h4>{popupData.student?.name}</h4>
                      <p>{popupData.student?.rollnumber} • {popupData.student?.department}</p>
                    </div>
                  </div>

                  <div className="gatepass-popup-badges">
                    <span className="gatepass-popup-type-badge outstation">OUTSTATION</span>
                    <span className="gatepass-popup-number-badge">{popupData.gatePassNo}</span>
                    <span className={`gatepass-popup-status-badge ${getOutstationLifecycleStatus(popupData.gatepassDetails)}`}>
                      {getGatepassStatusLabel(getOutstationLifecycleStatus(popupData.gatepassDetails))}
                    </span>
                  </div>

                  <div className="gatepass-popup-details">
                    <div className="gatepass-popup-detail-item">
                      <span className="gatepass-popup-detail-label">Nature of Leave</span>
                      <span className="gatepass-popup-detail-value">{popupData.gatepassDetails?.natureOfLeave || '--'}</span>
                    </div>
                    <div className="gatepass-popup-detail-item">
                      <span className="gatepass-popup-detail-label">Leave Days</span>
                      <span className="gatepass-popup-detail-value">{popupData.gatepassDetails?.leaveDays || '--'}</span>
                    </div>
                    <div className="gatepass-popup-detail-item full-width">
                      <span className="gatepass-popup-detail-label">Reason</span>
                      <span className="gatepass-popup-detail-value">{popupData.gatepassDetails?.reasonOfLeave || '--'}</span>
                    </div>
                    <div className="gatepass-popup-detail-item full-width">
                      <span className="gatepass-popup-detail-label">Address During Leave</span>
                      <span className="gatepass-popup-detail-value">{popupData.gatepassDetails?.address || '--'}</span>
                    </div>
                  </div>

                  <div className="gatepass-popup-utilization">
                    <h5>Scheduled Times</h5>
                    <div className="gatepass-popup-util-grid">
                      <div className="gatepass-popup-util-item exit">
                        <span className="gatepass-popup-util-label">Scheduled Exit</span>
                        <span className="gatepass-popup-util-value scheduled">
                          {formatScheduledDateTime(popupData.gatepassDetails?.dateOut, popupData.gatepassDetails?.timeOut)}
                        </span>
                      </div>
                      <div className="gatepass-popup-util-item entry">
                        <span className="gatepass-popup-util-label">Scheduled Entry</span>
                        <span className="gatepass-popup-util-value scheduled">
                          {formatScheduledDateTime(popupData.gatepassDetails?.dateIn, popupData.gatepassDetails?.timeIn)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="gatepass-popup-utilization">
                    <h5>Utilization Status</h5>
                    <div className="gatepass-popup-util-grid">
                      <div className="gatepass-popup-util-item exit">
                        <span className="gatepass-popup-util-label">Actual Exit</span>
                        <span className={`gatepass-popup-util-value ${!popupData.gatepassDetails?.actualExitAt ? 'not-done' : ''}`}>
                          {popupData.gatepassDetails?.actualExitAt ? formatDateTime(popupData.gatepassDetails.actualExitAt) : 'Not Done Yet'}
                        </span>
                      </div>
                      <div className="gatepass-popup-util-item entry">
                        <span className="gatepass-popup-util-label">Actual Entry</span>
                        <span className={`gatepass-popup-util-value ${!popupData.gatepassDetails?.actualEntryAt ? 'not-done' : ''}`}>
                          {popupData.gatepassDetails?.actualEntryAt ? formatDateTime(popupData.gatepassDetails.actualEntryAt) : 'Not Done Yet'}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="gatepass-popup-timeline">
                    <h5>Approval Timeline</h5>
                    <div className="gatepass-popup-timeline-list">
                      <div className="gatepass-popup-timeline-item">
                        <span className="gatepass-popup-timeline-stage">Applied</span>
                        <span className="gatepass-popup-timeline-time">
                          {formatDateTime(popupData.gatepassDetails?.appliedAt) || '--'}
                        </span>
                      </div>
                      {popupData.gatepassDetails?.approvedAt && (
                        <div className={`gatepass-popup-timeline-item ${popupData.gatepassDetails?.finalStatus === 'approved' ? 'approved' : 'rejected'}`}>
                          <span className="gatepass-popup-timeline-stage">
                            Hostel Office {popupData.gatepassDetails?.finalStatus === 'approved' ? 'Approved' : 'Rejected'}
                          </span>
                          <span className="gatepass-popup-timeline-time">
                            {formatDateTime(popupData.gatepassDetails.approvedAt)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  <GatepassExtraInfo details={popupData.gatepassDetails} />
                  <GatepassStageNotes details={popupData.gatepassDetails} />
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Student ID Card Popup */}
      <StudentIdCardPopup
        student={selectedStudent}
        isOpen={showIdCard}
        onClose={() => setShowIdCard(false)}
      />
    </div>
  );
};

// ==================== GATEPASS REGISTER VIEW ====================

const GatepassRegisterView = () => {
  const today = new Date().toISOString().split('T')[0];
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [search, setSearch] = useState('');
  const [gpTypeFilter, setGpTypeFilter] = useState('');
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  const [showPopup, setShowPopup] = useState(false);
  const [popupLoading, setPopupLoading] = useState(false);
  const [popupType, setPopupType] = useState(null);
  const [popupData, setPopupData] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showIdCard, setShowIdCard] = useState(false);

  const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return '';
    const datePart = d.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
    const timePart = d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
    return `${datePart}, ${timePart}`;
  };

  const handleGatePassClick = async (gatePassNo) => {
    if (!gatePassNo || gatePassNo === '--') return;
    setShowPopup(true);
    setPopupLoading(true);
    setPopupData(null);
    setPopupType(null);
    try {
      const res = await getGatepassByNumber(gatePassNo);
      const type = res.data.type;
      const gp = res.data.gatepass;
      setPopupType(type);
      if (type === 'LOCAL') {
        setPopupData({
          student: {
            ...gp.student,
            name: gp.studentName || gp.student?.name,
            rollnumber: gp.rollnumber || gp.student?.rollnumber,
            course: gp.course || gp.student?.course,
            branch: gp.branch || gp.student?.branch || gp.department || gp.student?.department,
            department: gp.department || gp.student?.department,
            roomNumber: gp.roomNumber || gp.student?.roomNumber,
            hostelName: gp.hostelName || gp.student?.hostelName,
            contact: gp.contact || gp.student?.contactNumber,
            contactNumber: gp.contact || gp.student?.contactNumber,
            presence: gp.student?.presence,
          },
          gatePassNo: gp.gatePassNo,
          gatepassDetails: {
            purpose: gp.purpose,
            place: gp.place,
            status: gp.status,
            dateOut: gp.dateOut,
            dateIn: gp.dateIn,
            timeOut: gp.timeOut,
            timeIn: gp.timeIn,
            actualExitAt: gp.actualExitAt,
            actualEntryAt: gp.actualEntryAt,
            appliedAt: gp.createdAt,
            approvedAt: gp.decidedAt,
            rejectionReason: gp.rejectionReason,
          },
        });
      } else {
        setPopupData({
          student: {
            ...gp.student,
            name: gp.studentName || gp.student?.name,
            rollnumber: gp.rollnumber || gp.student?.rollnumber,
            course: gp.course || gp.student?.course,
            branch: gp.branch || gp.student?.branch || gp.department || gp.student?.department,
            department: gp.department || gp.student?.department,
            roomNumber: gp.roomNumber || gp.student?.roomNumber,
            hostelName: gp.hostelName || gp.student?.hostelName,
            contact: gp.contact || gp.student?.contactNumber,
            contactNumber: gp.contact || gp.student?.contactNumber,
            presence: gp.student?.presence,
          },
          gatePassNo: gp.gatePassNo,
          gatepassDetails: {
            natureOfLeave: gp.natureOfLeave,
            leaveDays: gp.leaveDays,
            reasonOfLeave: gp.reasonOfLeave,
            address: gp.address,
            status: gp.currentStage,
            finalStatus: gp.finalStatus,
            utilizationStatus: gp.utilizationStatus,
            actualExitAt: gp.actualExitAt,
            actualEntryAt: gp.actualEntryAt,
            appliedAt: gp.createdAt,
            approvedAt: gp.stageStatus?.hostelOffice?.decidedAt,
            course: gp.course,
            stageStatus: gp.stageStatus,
            dateOut: gp.dateOut,
            timeOut: gp.timeOut,
            dateIn: gp.dateIn,
            timeIn: gp.timeIn,
            instructorNote: gp.instructorNote,
            officeSecretaryNote: gp.officeSecretaryNote,
            dugcNote: gp.dugcNote,
            dpgcNote: gp.dpgcNote,
            hodNote: gp.hodNote,
            deanNote: gp.deanNote,
            previousLeavesTaken: gp.previousLeavesTaken,
            classesMissed: gp.classesMissed,
            missedDays: gp.missedDays,
            rejectionReason: gp.rejectionReason,
          },
        });
      }
    } catch (err) {
      setPopupData({ error: err.response?.data?.message || 'Failed to load gatepass details' });
    } finally {
      setPopupLoading(false);
    }
  };

  const closePopup = () => {
    setShowPopup(false);
    setPopupData(null);
    setPopupType(null);
  };

  useEffect(() => {
    const fetchLogs = async () => {
      if (fromDate && toDate && fromDate > toDate) {
        setLogs([]);
        return;
      }
      setLoading(true);
      try {
        const res = await getGatepassRegisterLogs(fromDate || undefined, toDate || undefined, search || undefined, gpTypeFilter || undefined);
        setLogs(res.data.logs || []);
      } catch (err) {
        console.error('Failed to fetch gatepass register', err);
      } finally {
        setLoading(false);
      }
    };
    const t = setTimeout(fetchLogs, 300);
    return () => clearTimeout(t);
  }, [fromDate, toDate, search, gpTypeFilter]);

  const formatLogTime = (dateStr) => {
    if (!dateStr) return '--';
    const d = new Date(dateStr);
    return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }).toUpperCase();
  };
  const formatLogDate = (dateStr) => {
    if (!dateStr) return '--';
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return '--';
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = String(d.getFullYear()).slice(-2);
    return `${day}/${month}/${year}`;
  };

  const handleDownloadExcel = () => {
    if (!logs.length) return;
    const headers = ['Sr No', 'Name', 'Roll No', 'Room No', 'Contact', 'Place', 'Purpose', 'Gate Pass', 'Time Out', 'Exit Date', 'Time In', 'Entry Date'];
    const escapeCSV = (val) => {
      const str = String(val);
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };
    const rows = logs.map(log => {
      const contact = log.contact ? `="${log.contact}"` : '';
      return [log.srNo || '', log.name || '', log.rollNo || '', log.roomNo || '', contact, log.place || '', log.purpose || '', log.gatePass || '--', formatLogTime(log.timeOut), formatLogDate(log.timeOut), formatLogTime(log.timeIn), formatLogDate(log.timeIn)]
        .map(escapeCSV).join(',');
    });
    const BOM = '\uFEFF';
    const csvContent = BOM + [headers.join(','), ...rows].join('\r\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `GatepassRegister_${fromDate || 'all'}_to_${toDate || 'all'}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getRowStyle = (log) => {
    const flags = log.flags || {};
    if (flags.lateGatepass) return { backgroundColor: '#fee2e2' };
    if (flags.outside) return { backgroundColor: '#fef3c7' };
    return {};
  };

  return (
    <div className="os-section" style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', maxWidth: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap', gap: '8px', flexShrink: 0 }}>
        <h2 className="os-section-title" style={{ margin: 0 }}>Gatepass Register</h2>
        <div style={{ display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label style={{ fontWeight: '500', fontSize: '14px', color: '#666' }}>FROM</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              max={toDate || new Date().toISOString().split('T')[0]}
              style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px' }}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label style={{ fontWeight: '500', fontSize: '14px', color: '#666' }}>TO</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              min={fromDate || undefined}
              max={new Date().toISOString().split('T')[0]}
              style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px' }}
            />
            {(fromDate || toDate) && (
              <button
                onClick={() => { setFromDate(''); setToDate(''); }}
                style={{ padding: '8px 12px', background: '#f3f4f6', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}
              >
                All
              </button>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label style={{ fontWeight: '500', fontSize: '14px', color: '#666' }}>SEARCH</label>
            <input
              type="text"
              placeholder="Name, Roll or GP No..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', minWidth: '180px' }}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label style={{ fontWeight: '500', fontSize: '14px', color: '#666' }}>TYPE</label>
            <select
              value={gpTypeFilter}
              onChange={(e) => setGpTypeFilter(e.target.value)}
              style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', minWidth: '150px' }}
            >
              <option value="">All Gatepasses</option>
              <option value="local">Local Gatepass</option>
              <option value="outstation">Outstation Gatepass</option>
            </select>
          </div>
          <button
            onClick={handleDownloadExcel}
            disabled={logs.length === 0}
            style={{
              background: logs.length === 0 ? '#9ca3af' : '#10b981',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '6px',
              cursor: logs.length === 0 ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            📥 Download Excel
          </button>
        </div>
      </div>

      <div style={{ background: 'white', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <div style={{ overflowY: 'auto', overflowX: 'hidden', flex: 1 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px', tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: '38px' }} />
              <col style={{ width: '13%' }} />
              <col style={{ width: '8%' }} />
              <col style={{ width: '5%' }} />
              <col style={{ width: '9%' }} />
              <col style={{ width: '9.5%' }} />
              <col style={{ width: '8.5%' }} />
              <col style={{ width: '7.5%' }} />
              <col style={{ width: '7.5%' }} />
              <col style={{ width: '7.5%' }} />
              <col style={{ width: '7.5%' }} />
              <col style={{ width: '7.5%' }} />
            </colgroup>
            <thead>
              <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #e5e7eb' }}>
                <th style={{ padding: '10px 6px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '10px' }}>SR</th>
                <th style={{ padding: '10px 6px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '10px' }}>NAME</th>
                <th style={{ padding: '10px 6px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '10px' }}>ROLL NO.</th>
                <th style={{ padding: '10px 6px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '10px' }}>ROOM</th>
                <th style={{ padding: '10px 6px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '10px' }}>CONTACT</th>
                <th style={{ padding: '10px 6px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '10px' }}>PLACE</th>
                <th style={{ padding: '10px 6px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '10px' }}>PURPOSE</th>
                <th style={{ padding: '10px 6px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '10px' }}>GATE PASS</th>
                <th style={{ padding: '10px 6px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '10px' }}>TIME OUT</th>
                <th style={{ padding: '10px 6px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '10px' }}>EXIT DATE</th>
                <th style={{ padding: '10px 6px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '10px' }}>TIME IN</th>
                <th style={{ padding: '10px 6px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '10px' }}>ENTRY DATE</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={12} style={{ padding: '40px', textAlign: 'center', color: '#666' }}>Loading...</td></tr>
              )}
              {!loading && logs.length === 0 && (
                <tr><td colSpan={12} style={{ padding: '40px', textAlign: 'center', color: '#666' }}>No gatepass records found</td></tr>
              )}
              {!loading && logs.map((log) => {
                const rowStyle = getRowStyle(log);
                return (
                  <tr key={log._id} style={{ borderBottom: '1px solid #e5e7eb', ...rowStyle }}>
                    <td style={{ padding: '8px 6px', color: '#ef4444', fontWeight: '500', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{log.srNo}</td>
                    <td style={{ padding: '8px 6px', color: '#111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{log.name}</td>
                    <td style={{ padding: '8px 6px', color: '#111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{log.rollNo}</td>
                    <td style={{ padding: '8px 6px', color: '#111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{log.roomNo}</td>
                    <td style={{ padding: '8px 6px', color: '#111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{log.contact}</td>
                    <td style={{ padding: '8px 6px', color: '#2563eb', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{log.place}</td>
                    <td style={{ padding: '8px 6px', color: '#059669', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{log.purpose}</td>
                    <td style={{ padding: '8px 6px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {log.gatePass && log.gatePass !== '--' ? (
                        <button
                          onClick={() => handleGatePassClick(log.gatePass)}
                          title="View full gatepass details"
                          style={{
                            background: '#eef2ff',
                            color: '#1d4ed8',
                            border: '1px solid #c7d2fe',
                            padding: '3px 8px',
                            borderRadius: '5px',
                            fontSize: '11px',
                            fontWeight: 600,
                            cursor: 'pointer',
                            fontFamily: 'inherit',
                          }}
                        >
                          {log.gatePass}
                        </button>
                      ) : (
                        <span style={{ color: '#666' }}>--</span>
                      )}
                    </td>
                    <td style={{ padding: '8px 6px', color: '#111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{formatLogTime(log.timeOut)}</td>
                    <td style={{ padding: '8px 6px', color: '#111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{formatLogDate(log.timeOut)}</td>
                    <td style={{ padding: '8px 6px', color: log.timeIn ? '#059669' : '#999', fontWeight: log.timeIn ? '500' : 'normal', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{formatLogTime(log.timeIn)}</td>
                    <td style={{ padding: '8px 6px', color: log.timeIn ? '#059669' : '#999', fontWeight: log.timeIn ? '500' : 'normal', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{formatLogDate(log.timeIn)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {showPopup && popupType === 'LOCAL' && (
        <div className="gatepass-popup-overlay" onClick={closePopup}>
          <div className="gatepass-popup-content" onClick={(e) => e.stopPropagation()}>
            <div className="gatepass-popup-header">
              <h3>Local Gatepass Details</h3>
              <button className="gatepass-popup-close" onClick={closePopup}>×</button>
            </div>
            <div className="gatepass-popup-body">
              {popupLoading && <div className="os-loading">Loading gatepass details...</div>}
              {!popupLoading && popupData?.error && (<div className="os-error">{popupData.error}</div>)}
              {!popupLoading && popupData && !popupData.error && (
                <>
                  <div className="gatepass-popup-student">
                    <div
                      className="gatepass-popup-avatar profile-pic-hover"
                      onClick={() => { setSelectedStudent(popupData.student); setShowIdCard(true); }}
                      title="Click to view GoThru ID Card"
                    >
                      {popupData.student?.imageUrl ? (
                        <img src={`${API_BASE}${popupData.student.imageUrl}`} alt="" />
                      ) : (
                        <div className="gatepass-popup-avatar-placeholder">{popupData.student?.name?.charAt(0) || '?'}</div>
                      )}
                    </div>
                    <div className="gatepass-popup-student-info">
                      <h4>{popupData.student?.name}</h4>
                      <p>{popupData.student?.rollnumber} • {popupData.student?.department}</p>
                    </div>
                  </div>
                  <div className="gatepass-popup-badges">
                    <span className="gatepass-popup-type-badge local">LOCAL</span>
                    <span className="gatepass-popup-number-badge">{popupData.gatePassNo}</span>
                    <span className={`gatepass-popup-status-badge ${popupData.gatepassDetails?.status}`}>{popupData.gatepassDetails?.status || 'Pending'}</span>
                  </div>
                  <div className="gatepass-popup-details">
                    <div className="gatepass-popup-detail-item">
                      <span className="gatepass-popup-detail-label">Purpose</span>
                      <span className="gatepass-popup-detail-value">{popupData.gatepassDetails?.purpose || '--'}</span>
                    </div>
                    <div className="gatepass-popup-detail-item">
                      <span className="gatepass-popup-detail-label">Place</span>
                      <span className="gatepass-popup-detail-value">{popupData.gatepassDetails?.place || '--'}</span>
                    </div>
                  </div>
                  <div className="gatepass-popup-utilization">
                    <h5>Scheduled Times</h5>
                    <div className="gatepass-popup-util-grid">
                      <div className="gatepass-popup-util-item exit">
                        <span className="gatepass-popup-util-label">Scheduled Exit</span>
                        <span className="gatepass-popup-util-value scheduled">{formatScheduledDateTime(popupData.gatepassDetails?.dateOut, popupData.gatepassDetails?.timeOut)}</span>
                      </div>
                      <div className="gatepass-popup-util-item entry">
                        <span className="gatepass-popup-util-label">Scheduled Entry</span>
                        <span className="gatepass-popup-util-value scheduled">{formatScheduledDateTime(popupData.gatepassDetails?.dateIn, popupData.gatepassDetails?.timeIn)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="gatepass-popup-utilization">
                    <h5>Actual Times</h5>
                    <div className="gatepass-popup-util-grid">
                      <div className="gatepass-popup-util-item exit">
                        <span className="gatepass-popup-util-label">Actual Exit</span>
                        <span className={`gatepass-popup-util-value ${!popupData.gatepassDetails?.actualExitAt ? 'not-done' : ''}`}>
                          {popupData.gatepassDetails?.actualExitAt ? formatDateTime(popupData.gatepassDetails.actualExitAt) : 'Not Done Yet'}
                        </span>
                      </div>
                      <div className="gatepass-popup-util-item entry">
                        <span className="gatepass-popup-util-label">Actual Entry</span>
                        <span className={`gatepass-popup-util-value ${!popupData.gatepassDetails?.actualEntryAt ? 'not-done' : ''}`}>
                          {popupData.gatepassDetails?.actualEntryAt ? formatDateTime(popupData.gatepassDetails.actualEntryAt) : 'Not Done Yet'}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="gatepass-popup-timeline">
                    <h5>Timeline</h5>
                    <div className="gatepass-popup-timeline-list">
                      <div className="gatepass-popup-timeline-item">
                        <span className="gatepass-popup-timeline-stage">Applied</span>
                        <span className="gatepass-popup-timeline-time">{formatDateTime(popupData.gatepassDetails?.appliedAt) || '--'}</span>
                      </div>
                      {popupData.gatepassDetails?.approvedAt && (
                        <div className={`gatepass-popup-timeline-item ${popupData.gatepassDetails?.status === 'denied' ? 'denied' : 'approved'}`}>
                          <span className="gatepass-popup-timeline-stage">{popupData.gatepassDetails?.status === 'denied' ? 'Denied' : 'Approved'}</span>
                          <span className="gatepass-popup-timeline-time">{formatDateTime(popupData.gatepassDetails.approvedAt)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  {popupData.gatepassDetails?.status === 'denied' && (
                    <div className="gatepass-popup-details">
                      <div className="gatepass-popup-detail-item full-width">
                        <span className="gatepass-popup-detail-label">Rejection Reason</span>
                        <span className="gatepass-popup-detail-value">{popupData.gatepassDetails?.rejectionReason || '—'}</span>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {showPopup && popupType === 'OUTSTATION' && (
        <div className="gatepass-popup-overlay" onClick={closePopup}>
          <div className="gatepass-popup-content" onClick={(e) => e.stopPropagation()}>
            <div className="gatepass-popup-header">
              <h3>Outstation Gatepass Details</h3>
              <button className="gatepass-popup-close" onClick={closePopup}>×</button>
            </div>
            <div className="gatepass-popup-body">
              {popupLoading && <div className="os-loading">Loading gatepass details...</div>}
              {!popupLoading && popupData?.error && (<div className="os-error">{popupData.error}</div>)}
              {!popupLoading && popupData && !popupData.error && (
                <>
                  <div className="gatepass-popup-student">
                    <div
                      className="gatepass-popup-avatar profile-pic-hover"
                      onClick={() => { setSelectedStudent(popupData.student); setShowIdCard(true); }}
                      title="Click to view GoThru ID Card"
                    >
                      {popupData.student?.imageUrl ? (
                        <img src={`${API_BASE}${popupData.student.imageUrl}`} alt="" />
                      ) : (
                        <div className="gatepass-popup-avatar-placeholder">{popupData.student?.name?.charAt(0) || '?'}</div>
                      )}
                    </div>
                    <div className="gatepass-popup-student-info">
                      <h4>{popupData.student?.name}</h4>
                      <p>{popupData.student?.rollnumber} • {popupData.student?.department}</p>
                    </div>
                  </div>
                  <div className="gatepass-popup-badges">
                    <span className="gatepass-popup-type-badge outstation">OUTSTATION</span>
                    <span className="gatepass-popup-number-badge">{popupData.gatePassNo}</span>
                    <span className={`gatepass-popup-status-badge ${getOutstationLifecycleStatus(popupData.gatepassDetails)}`}>
                      {getGatepassStatusLabel(getOutstationLifecycleStatus(popupData.gatepassDetails))}
                    </span>
                  </div>
                  <div className="gatepass-popup-details">
                    <div className="gatepass-popup-detail-item">
                      <span className="gatepass-popup-detail-label">Nature of Leave</span>
                      <span className="gatepass-popup-detail-value">{popupData.gatepassDetails?.natureOfLeave || '--'}</span>
                    </div>
                    <div className="gatepass-popup-detail-item">
                      <span className="gatepass-popup-detail-label">Leave Days</span>
                      <span className="gatepass-popup-detail-value">{popupData.gatepassDetails?.leaveDays || '--'}</span>
                    </div>
                    <div className="gatepass-popup-detail-item full-width">
                      <span className="gatepass-popup-detail-label">Reason</span>
                      <span className="gatepass-popup-detail-value">{popupData.gatepassDetails?.reasonOfLeave || '--'}</span>
                    </div>
                    <div className="gatepass-popup-detail-item full-width">
                      <span className="gatepass-popup-detail-label">Address During Leave</span>
                      <span className="gatepass-popup-detail-value">{popupData.gatepassDetails?.address || '--'}</span>
                    </div>
                  </div>
                  <div className="gatepass-popup-utilization">
                    <h5>Scheduled Times</h5>
                    <div className="gatepass-popup-util-grid">
                      <div className="gatepass-popup-util-item exit">
                        <span className="gatepass-popup-util-label">Scheduled Exit</span>
                        <span className="gatepass-popup-util-value scheduled">{formatScheduledDateTime(popupData.gatepassDetails?.dateOut, popupData.gatepassDetails?.timeOut)}</span>
                      </div>
                      <div className="gatepass-popup-util-item entry">
                        <span className="gatepass-popup-util-label">Scheduled Entry</span>
                        <span className="gatepass-popup-util-value scheduled">{formatScheduledDateTime(popupData.gatepassDetails?.dateIn, popupData.gatepassDetails?.timeIn)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="gatepass-popup-utilization">
                    <h5>Utilization Status</h5>
                    <div className="gatepass-popup-util-grid">
                      <div className="gatepass-popup-util-item exit">
                        <span className="gatepass-popup-util-label">Actual Exit</span>
                        <span className={`gatepass-popup-util-value ${!popupData.gatepassDetails?.actualExitAt ? 'not-done' : ''}`}>
                          {popupData.gatepassDetails?.actualExitAt ? formatDateTime(popupData.gatepassDetails.actualExitAt) : 'Not Done Yet'}
                        </span>
                      </div>
                      <div className="gatepass-popup-util-item entry">
                        <span className="gatepass-popup-util-label">Actual Entry</span>
                        <span className={`gatepass-popup-util-value ${!popupData.gatepassDetails?.actualEntryAt ? 'not-done' : ''}`}>
                          {popupData.gatepassDetails?.actualEntryAt ? formatDateTime(popupData.gatepassDetails.actualEntryAt) : 'Not Done Yet'}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="gatepass-popup-timeline">
                    <h5>Approval Timeline</h5>
                    <div className="gatepass-popup-timeline-list">
                      <div className="gatepass-popup-timeline-item">
                        <span className="gatepass-popup-timeline-stage">Applied</span>
                        <span className="gatepass-popup-timeline-time">{formatDateTime(popupData.gatepassDetails?.appliedAt) || '--'}</span>
                      </div>
                      {popupData.gatepassDetails?.approvedAt && (
                        <div className={`gatepass-popup-timeline-item ${popupData.gatepassDetails?.finalStatus === 'approved' ? 'approved' : 'rejected'}`}>
                          <span className="gatepass-popup-timeline-stage">Hostel Office {popupData.gatepassDetails?.finalStatus === 'approved' ? 'Approved' : 'Rejected'}</span>
                          <span className="gatepass-popup-timeline-time">{formatDateTime(popupData.gatepassDetails.approvedAt)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <GatepassExtraInfo details={popupData.gatepassDetails} />
                  <GatepassStageNotes details={popupData.gatepassDetails} />
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <StudentIdCardPopup
        student={selectedStudent}
        isOpen={showIdCard}
        onClose={() => setShowIdCard(false)}
      />
    </div>
  );
};

// ==================== MID/END SEM BREAK GATEPASS VIEWS ====================

const SBRequestsView = ({ onViewDetails }) => {
  const [gatepasses, setGatepasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showIdCard, setShowIdCard] = useState(false);

  const handleProfileClick = (student, gatepass) => {
    setSelectedStudent({
      ...student,
      name: gatepass.studentName,
      rollnumber: gatepass.rollnumber,
      branch: gatepass.branch || gatepass.department,
      department: gatepass.department,
      contact: gatepass.contact,
      roomNumber: gatepass.roomNumber,
      hostelName: gatepass.hostelName,
      course: gatepass.course,
    });
    setShowIdCard(true);
  };

  const fetchGatepasses = async () => {
    try {
      setError('');
      const res = await getHostelOfficeSBPendingGatepasses();
      setGatepasses(res.data.gatepasses || []);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to fetch gatepasses');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGatepasses();
  }, []);

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    let hours = d.getHours();
    const mins = String(d.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    return `${day}/${month}/${year},  ${hours}:${mins} ${ampm}`;
  };

  const sortedGatepasses = [...gatepasses].sort((a, b) =>
    new Date(a.createdAt) - new Date(b.createdAt)
  );

  const normalizedSearch = search.trim().toLowerCase();
  const filteredGatepasses = normalizedSearch
    ? sortedGatepasses.filter((gp) => {
        const name = gp.studentName || gp.student?.name || '';
        const roll = gp.rollnumber || gp.student?.rollnumber || '';
        return `${name} ${roll}`.toLowerCase().includes(normalizedSearch);
      })
    : sortedGatepasses;

  return (
    <div className="os-section">
      <div className="os-history-header-row">
        <h2 className="os-section-title">Pending Mid/End Sem Break Requests</h2>
        <form className="os-search-form" onSubmit={(e) => e.preventDefault()}>
          <input
            type="text"
            placeholder="Search by name or roll number..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="os-search-input"
            aria-label="Search by name or roll number"
          />
        </form>
      </div>

      {loading && <div className="os-loading">Loading requests...</div>}
      {error && <div className="os-error">{error}</div>}

      {!loading && gatepasses.length === 0 && (
        <div className="os-empty">No pending mid/end sem break requests</div>
      )}

      {!loading && gatepasses.length > 0 && filteredGatepasses.length === 0 && (
        <div className="os-empty">No matches found</div>
      )}

      <div className="os-cards-grid">
        {filteredGatepasses.map((gp) => (
          <div key={gp._id} className="os-request-card">
            <div
              className="os-card-avatar profile-pic-hover"
              onClick={() => handleProfileClick(gp.student, gp)}
              title="Click to view GoThru ID Card"
            >
              {gp.student?.imageUrl ? (
                <img
                  src={getImageUrl(gp.student.imageUrl)}
                  alt={gp.studentName}
                  className="os-card-avatar-img"
                />
              ) : (
                <span style={{ fontSize: '20px', color: '#fff' }}>●</span>
              )}
            </div>
            <div className="os-card-info">
              <div className="os-card-name">{gp.studentName}</div>
              <div className="os-card-details">
                <span>{gp.rollnumber}</span>
              </div>
              <div className="os-card-branch">{gp.course} • {gp.department}</div>
              <div className="os-card-applied">Applied: {formatDateTime(gp.createdAt)}</div>
            </div>
            <button className="os-view-btn" onClick={() => onViewDetails(gp._id)}>
              View Details
            </button>
          </div>
        ))}
      </div>

      <StudentIdCardPopup
        student={selectedStudent}
        isOpen={showIdCard}
        onClose={() => setShowIdCard(false)}
      />
    </div>
  );
};

const SBGatepassDetailsView = ({ gatepassId, onBack }) => {
  const [gatepass, setGatepass] = useState(null);
  const [studentHistory, setStudentHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [popupMessage, setPopupMessage] = useState('');
  const [deciding, setDeciding] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ open: false, decision: null });
  const [showIdCard, setShowIdCard] = useState(false);

  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        setError('');
        const res = await getHostelOfficeSBGatepassDetails(gatepassId);
        setGatepass(res.data.gatepass || null);

        if (res.data.gatepass?.student) {
          setHistoryLoading(true);
          try {
            const studentId = typeof res.data.gatepass.student === 'object' ? res.data.gatepass.student._id : res.data.gatepass.student;
            const historyRes = await getHostelOfficeSBStudentHistory(studentId);
            setStudentHistory(historyRes.data.gatepasses || []);
          } catch (histErr) {
            console.error('Failed to fetch student history:', histErr);
          } finally {
            setHistoryLoading(false);
          }
        }
      } catch (err) {
        setError(err?.response?.data?.message || 'Failed to fetch details');
      } finally {
        setLoading(false);
      }
    };
    fetchDetails();
  }, [gatepassId]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '--';
    const parts = dateStr.split('-');
    return parts.length === 3 ? `${parseInt(parts[2], 10)} ${monthNames[parseInt(parts[1], 10) - 1]} ${parts[0]}` : dateStr;
  };

  const formatTime12hr = (timeStr) => {
    if (!timeStr) return '';
    const parts = timeStr.split(':');
    if (parts.length < 2) return timeStr;
    let hours = parseInt(parts[0], 10);
    const minutes = parts[1];
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    return `${hours}:${minutes} ${ampm}`;
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const day = d.getDate();
    const month = monthNames[d.getMonth()];
    let hours = d.getHours();
    const mins = String(d.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    return `${day} ${month}, ${hours}:${mins} ${ampm}`;
  };

  const openConfirmModal = (decision) => {
    setConfirmModal({ open: true, decision });
  };

  const closeConfirmModal = () => {
    setConfirmModal({ open: false, decision: null });
  };

  const handleDecision = async (decision) => {
    setDeciding(true);
    try {
      const payload = { gatepassId, decision };
      if (decision === 'rejected' && confirmModal.rejectionReason) {
        payload.rejectionReason = confirmModal.rejectionReason;
      }
      const res = await decideHostelOfficeSBGatepass(payload);
      closeConfirmModal();
      setPopupMessage(res.data.message);
      setTimeout(() => onBack(), 1500);
    } catch (err) {
      setPopupMessage(err?.response?.data?.message || 'Failed to process decision');
      closeConfirmModal();
    } finally {
      setDeciding(false);
    }
  };

  if (loading) return <div className="os-loading">Loading details...</div>;
  if (error) return <div className="os-error">{error}</div>;
  if (!gatepass) return <div className="os-error">Gatepass not found</div>;

  return (
    <div className="os-details-view">
      <button className="os-back-btn" onClick={onBack}>
        ← Back to Requests
      </button>

      <h2 className="os-section-title">Mid/End Sem Break Gatepass Details</h2>

      <div className="os-details-card">
        <div className="os-student-info-header">
          <div
            className="os-student-avatar-large profile-pic-hover"
            onClick={() => setShowIdCard(true)}
            title="Click to view GoThru ID Card"
          >
            {gatepass.student?.imageUrl ? (
              <img
                src={getImageUrl(gatepass.student.imageUrl)}
                alt={gatepass.studentName}
              />
            ) : (
              <span className="os-avatar-placeholder">{gatepass.studentName?.charAt(0)?.toUpperCase() || '?'}</span>
            )}
          </div>
          <div className="os-student-header-info">
            <h3 className="os-student-name-large">{gatepass.studentName}</h3>
            <span className="os-student-roll-large">{gatepass.rollnumber}</span>
          </div>
        </div>

        <div className="os-details-grid">
          <div className="os-detail-item">
            <span className="os-detail-label">Course</span>
            <span className="os-detail-value">{gatepass.course || '--'}</span>
          </div>
          <div className="os-detail-item">
            <span className="os-detail-label">Department</span>
            <span className="os-detail-value">{gatepass.department || '--'}</span>
          </div>
          <div className="os-detail-item">
            <span className="os-detail-label">Room / Hostel</span>
            <span className="os-detail-value">{gatepass.roomNumber || '--'} / {gatepass.hostelName || '--'}</span>
          </div>
          <div className="os-detail-item">
            <span className="os-detail-label">Contact</span>
            <span className="os-detail-value">{gatepass.contact || '--'}</span>
          </div>
          <div className="os-detail-item">
            <span className="os-detail-label">Exit Date</span>
            <span className="os-detail-value">{formatDate(gatepass.dateOut)} {formatTime12hr(gatepass.timeOut)}</span>
          </div>
          <div className="os-detail-item">
            <span className="os-detail-label">Return Date</span>
            <span className="os-detail-value">{formatDate(gatepass.dateIn)} {formatTime12hr(gatepass.timeIn)}</span>
          </div>
          <div className="os-detail-item">
            <span className="os-detail-label">Leave Days</span>
            <span className="os-detail-value">{gatepass.leaveDays || '--'}</span>
          </div>
          <div className="os-detail-item">
            <span className="os-detail-label">Address</span>
            <span className="os-detail-value">{gatepass.address || '--'}</span>
          </div>
          <div className="os-detail-item">
            <span className="os-detail-label">Nature of Leave</span>
            <span className="os-detail-value">{gatepass.natureOfLeave || '--'}</span>
          </div>
          <div className="os-detail-item">
            <span className="os-detail-label">Reason</span>
            <span className="os-detail-value">{gatepass.reasonOfLeave || '--'}</span>
          </div>
        </div>

        {gatepass.proofFile && (
          <div style={{ marginTop: '16px' }}>
            <span className="os-detail-label">Supporting Document</span>
            <a href={`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000'}${gatepass.proofFile}`} target="_blank" rel="noreferrer" style={{ color: '#2563eb', textDecoration: 'underline' }}>
              View Attachment
            </a>
          </div>
        )}

        <div className="os-approval-timeline">
          <h4>Approval Timeline</h4>
          <div className="os-timeline-item">
            <span className="os-timeline-label">Student Applied</span>
            <span className="os-timeline-value">{formatDateTime(gatepass.createdAt)}</span>
          </div>
          <div className="os-timeline-item">
            <span className="os-timeline-label">Direct to Hostel Office</span>
            <span className="os-timeline-value" style={{ color: '#e67e22', fontWeight: 500 }}>Mid/End Sem Break — no intermediate stages</span>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="os-action-btns">
          <button
            className="os-reject-btn"
            onClick={() => openConfirmModal('rejected')}
            disabled={deciding}
          >
            Reject
          </button>
          <button
            className="os-approve-btn"
            onClick={() => openConfirmModal('approved')}
            disabled={deciding}
          >
            Approve
          </button>
        </div>
      </div>

      {/* Student's SB Gatepass History */}
      <div className="os-student-history-section">
        <h3 className="os-history-title">Student's Previous Mid/End Sem Break Gatepasses</h3>
        {historyLoading && <div className="os-loading">Loading history...</div>}
        {!historyLoading && studentHistory.length === 0 && (
          <div className="os-empty">No previous mid/end sem break gatepasses</div>
        )}
        {!historyLoading && studentHistory.length > 0 && (
          <div className="os-history-table local-history-table">
            <div className="local-history-header">
              <span>GP No.</span>
              <span>Status</span>
              <span>Reason</span>
              <span>Exit Date</span>
              <span>Return Date</span>
            </div>
            {studentHistory.map((gp) => (
              <div key={gp._id} className={`local-history-row ${gp.finalStatus}`}>
                <span className="local-history-gpno">{gp.gatePassNo || '--'}</span>
                <span className={`local-history-status ${gp.finalStatus}`}>
                  {gp.finalStatus === 'approved' ? 'Approved' : gp.finalStatus === 'rejected' ? 'Rejected' : gp.finalStatus}
                </span>
                <span className="local-history-place">{gp.reasonOfLeave || '--'}</span>
                <span className="local-history-time">{formatDate(gp.dateOut)}</span>
                <span className="local-history-time">{formatDate(gp.dateIn)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={confirmModal.open}
        onClose={closeConfirmModal}
        onConfirm={() => handleDecision(confirmModal.decision)}
        decision={confirmModal.decision}
        studentName={gatepass?.studentName}
        rollNumber={gatepass?.rollnumber}
        isLocal={false}
        place={gatepass?.address}
        purpose={gatepass?.reasonOfLeave}
        isProcessing={deciding}
      />

      <StudentIdCardPopup
        student={gatepass ? {
          ...gatepass.student,
          name: gatepass.studentName,
          rollnumber: gatepass.rollnumber,
          branch: gatepass.branch || gatepass.department,
          department: gatepass.department,
          contact: gatepass.contact,
          roomNumber: gatepass.roomNumber,
          hostelName: gatepass.hostelName,
        } : null}
        isOpen={showIdCard}
        onClose={() => setShowIdCard(false)}
      />

      <PopupBox message={popupMessage} onClose={() => setPopupMessage('')} />
    </div>
  );
};

const SBHistoryView = () => {
  const [gatepasses, setGatepasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showIdCard, setShowIdCard] = useState(false);

  // Popup state for gatepass details
  const [showPopup, setShowPopup] = useState(false);
  const [popupData, setPopupData] = useState(null);
  const [popupLoading, setPopupLoading] = useState(false);

  const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

  const handleProfileClick = (e, student, gp) => {
    e.stopPropagation();
    setSelectedStudent({
      ...student,
      name: gp.studentName,
      rollnumber: gp.rollnumber,
      branch: gp.branch || gp.department,
      department: gp.department,
      contact: gp.contact,
      roomNumber: gp.roomNumber,
      hostelName: gp.hostelName,
      course: gp.course,
    });
    setShowIdCard(true);
  };

  const fetchHistory = async (searchQuery = '') => {
    try {
      setError('');
      setLoading(true);
      const res = await getHostelOfficeSBGatepassHistory(searchQuery);
      setGatepasses(res.data.gatepasses || []);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to fetch history');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchHistory(search);
  };

  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  const formatDate = (dateStr) => {
    if (!dateStr) return '--';
    const parts = dateStr.split('-');
    if (parts.length === 3) return `${parseInt(parts[2], 10)} ${monthNames[parseInt(parts[1], 10) - 1]} ${parts[0]}`;
    return dateStr;
  };

  const formatTime12hr = (timeStr) => {
    if (!timeStr) return '';
    const parts = timeStr.split(':');
    if (parts.length < 2) return timeStr;
    let hours = parseInt(parts[0], 10);
    const minutes = parts[1];
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    return `${hours}:${minutes} ${ampm}`;
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    const datePart = d.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
    const timePart = d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
    return `${datePart}, ${timePart}`;
  };

  const handleRowClick = async (gp) => {
    if (!gp._id) return;
    setShowPopup(true);
    setPopupLoading(true);
    setPopupData(null);
    try {
      const res = await getHostelOfficeSBGatepassDetails(gp._id);
      const gatepass = res.data.gatepass;
      setPopupData({
        student: {
          ...gatepass.student,
          name: gatepass.studentName || gatepass.student?.name,
          rollnumber: gatepass.rollnumber || gatepass.student?.rollnumber,
          course: gatepass.course || gatepass.student?.course,
          branch: gatepass.branch || gatepass.student?.branch || gatepass.department || gatepass.student?.department,
          department: gatepass.department || gatepass.student?.department,
          roomNumber: gatepass.roomNumber || gatepass.student?.roomNumber,
          hostelName: gatepass.hostelName || gatepass.student?.hostelName,
          contact: gatepass.contact || gatepass.student?.contactNumber,
          contactNumber: gatepass.contact || gatepass.student?.contactNumber,
          presence: gatepass.student?.presence,
        },
        gatePassNo: gatepass.gatePassNo,
        gatepassDetails: {
          natureOfLeave: gatepass.natureOfLeave,
          leaveDays: gatepass.leaveDays,
          reasonOfLeave: gatepass.reasonOfLeave,
          address: gatepass.address,
          contact: gatepass.contact,
          roomNumber: gatepass.roomNumber,
          hostelName: gatepass.hostelName,
          course: gatepass.course,
          department: gatepass.department,
          dateOut: gatepass.dateOut,
          dateIn: gatepass.dateIn,
          timeOut: gatepass.timeOut,
          timeIn: gatepass.timeIn,
          finalStatus: gatepass.finalStatus,
          actualExitAt: gatepass.actualExitAt,
          actualEntryAt: gatepass.actualEntryAt,
          appliedAt: gatepass.createdAt,
          approvedAt: gatepass.stageStatus?.hostelOffice?.decidedAt,
          rejectionReason: gatepass.rejectionReason,
          stageStatus: gatepass.stageStatus,
          instructorNote: gatepass.instructorNote,
          officeSecretaryNote: gatepass.officeSecretaryNote,
          dugcNote: gatepass.dugcNote,
          dpgcNote: gatepass.dpgcNote,
          hodNote: gatepass.hodNote,
          deanNote: gatepass.deanNote,
          previousLeavesTaken: gatepass.previousLeavesTaken,
          classesMissed: gatepass.classesMissed,
          missedDays: gatepass.missedDays,
        },
      });
    } catch (err) {
      setPopupData({ error: err.response?.data?.message || 'Failed to load gatepass details' });
    } finally {
      setPopupLoading(false);
    }
  };

  const closePopup = () => {
    setShowPopup(false);
    setPopupData(null);
  };

  return (
    <div className="os-section">
      <div className="os-history-header-row">
        <h2 className="os-section-title">Mid/End Sem Break History</h2>
        <form onSubmit={handleSearch} className="os-search-form">
          <input
            type="text"
            placeholder="Search by name or roll number..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="os-search-input"
          />
          <button type="submit" className="os-search-btn">🔍</button>
        </form>
      </div>

      {loading && <div className="os-loading">Loading history...</div>}
      {error && <div className="os-error">{error}</div>}

      {!loading && gatepasses.length === 0 && (
        <div className="os-empty">No history found</div>
      )}

      <div className="os-history-table">
        {gatepasses.map((gp) => {
          const finalStatus = gp.finalStatus || 'pending';
          const hasGatepassNo = !!gp.gatePassNo;
          const isClickable = hasGatepassNo;

          return (
            <div
              key={gp._id}
              className={`os-history-row-enhanced ${finalStatus} ${isClickable ? 'os-history-row-clickable' : ''}`}
              onClick={() => isClickable && handleRowClick(gp)}
              role={isClickable ? 'button' : undefined}
              tabIndex={isClickable ? 0 : undefined}
            >
              <div
                className="os-history-avatar profile-pic-hover"
                onClick={(e) => handleProfileClick(e, gp.student, gp)}
                title="Click to view GoThru ID Card"
              >
                {gp.student?.imageUrl ? (
                  <img src={`${API_BASE}${gp.student.imageUrl}`} alt="" />
                ) : (
                  <div className="os-history-avatar-placeholder">
                    {gp.studentName?.charAt(0) || '?'}
                  </div>
                )}
              </div>

              <div className="os-history-student-info">
                <div className="os-history-name">{gp.studentName}</div>
                <div className="os-history-meta">{gp.rollnumber} • {gp.course} • {gp.department}</div>
              </div>

              {hasGatepassNo ? (
                <span className="os-history-gp-number">{gp.gatePassNo}</span>
              ) : finalStatus !== 'rejected' ? (
                <span className="os-history-gp-pending">Yet to be Approved</span>
              ) : null}

              <div className="os-history-leave-enhanced">
                <span>{gp.address || gp.reasonOfLeave}</span>
                <span className="os-history-reason-text">{gp.reasonOfLeave}</span>
              </div>

              <div className={`os-status-badge ${finalStatus}`}>
                {finalStatus?.toUpperCase()}
              </div>
            </div>
          );
        })}
      </div>

      {/* Gatepass Detail Popup */}
      {showPopup && (
        <div className="gatepass-popup-overlay" onClick={closePopup}>
          <div className="gatepass-popup-content" onClick={(e) => e.stopPropagation()}>
            <div className="gatepass-popup-header">
              <h3>Mid/End Sem Break Gatepass Details</h3>
              <button className="gatepass-popup-close" onClick={closePopup}>×</button>
            </div>
            <div className="gatepass-popup-body">
              {popupLoading && <div className="os-loading">Loading gatepass details...</div>}
              {!popupLoading && popupData?.error && (
                <div className="os-error">{popupData.error}</div>
              )}
              {!popupLoading && popupData && !popupData.error && (
                <>
                  <div className="gatepass-popup-student">
                    <div
                      className="gatepass-popup-avatar profile-pic-hover"
                      onClick={() => {
                        setSelectedStudent(popupData.student);
                        setShowIdCard(true);
                      }}
                      title="Click to view GoThru ID Card"
                    >
                      {popupData.student?.imageUrl ? (
                        <img src={`${API_BASE}${popupData.student.imageUrl}`} alt="" />
                      ) : (
                        <div className="gatepass-popup-avatar-placeholder">
                          {popupData.student?.name?.charAt(0) || '?'}
                        </div>
                      )}
                    </div>
                    <div className="gatepass-popup-student-info">
                      <h4>{popupData.student?.name}</h4>
                      <p>{popupData.student?.rollnumber} • {popupData.student?.department}</p>
                    </div>
                  </div>

                  <div className="gatepass-popup-badges">
                    <span className="gatepass-popup-type-badge outstation" style={{ background: '#e67e22' }}>SEM BREAK</span>
                    <span className="gatepass-popup-number-badge">{popupData.gatePassNo}</span>
                    <span className={`gatepass-popup-status-badge ${popupData.gatepassDetails?.finalStatus}`}>
                      {popupData.gatepassDetails?.finalStatus || 'Pending'}
                    </span>
                  </div>

                  <div className="gatepass-popup-details">
                    <div className="gatepass-popup-detail-item">
                      <span className="gatepass-popup-detail-label">Course</span>
                      <span className="gatepass-popup-detail-value">{popupData.gatepassDetails?.course || '--'}</span>
                    </div>
                    <div className="gatepass-popup-detail-item">
                      <span className="gatepass-popup-detail-label">Department</span>
                      <span className="gatepass-popup-detail-value">{popupData.gatepassDetails?.department || '--'}</span>
                    </div>
                    <div className="gatepass-popup-detail-item">
                      <span className="gatepass-popup-detail-label">Room / Hostel</span>
                      <span className="gatepass-popup-detail-value">{popupData.gatepassDetails?.roomNumber || '--'} / {popupData.gatepassDetails?.hostelName || '--'}</span>
                    </div>
                    <div className="gatepass-popup-detail-item">
                      <span className="gatepass-popup-detail-label">Contact</span>
                      <span className="gatepass-popup-detail-value">{popupData.gatepassDetails?.contact || '--'}</span>
                    </div>
                    <div className="gatepass-popup-detail-item">
                      <span className="gatepass-popup-detail-label">Exit</span>
                      <span className="gatepass-popup-detail-value">{formatDate(popupData.gatepassDetails?.dateOut)} {formatTime12hr(popupData.gatepassDetails?.timeOut)}</span>
                    </div>
                    <div className="gatepass-popup-detail-item">
                      <span className="gatepass-popup-detail-label">Return</span>
                      <span className="gatepass-popup-detail-value">{formatDate(popupData.gatepassDetails?.dateIn)} {formatTime12hr(popupData.gatepassDetails?.timeIn)}</span>
                    </div>
                    <div className="gatepass-popup-detail-item">
                      <span className="gatepass-popup-detail-label">Nature of Leave</span>
                      <span className="gatepass-popup-detail-value">{popupData.gatepassDetails?.natureOfLeave || '--'}</span>
                    </div>
                    <div className="gatepass-popup-detail-item">
                      <span className="gatepass-popup-detail-label">Leave Days</span>
                      <span className="gatepass-popup-detail-value">{popupData.gatepassDetails?.leaveDays || '--'}</span>
                    </div>
                    <div className="gatepass-popup-detail-item full-width">
                      <span className="gatepass-popup-detail-label">Reason</span>
                      <span className="gatepass-popup-detail-value">{popupData.gatepassDetails?.reasonOfLeave || '--'}</span>
                    </div>
                    <div className="gatepass-popup-detail-item full-width">
                      <span className="gatepass-popup-detail-label">Address During Leave</span>
                      <span className="gatepass-popup-detail-value">{popupData.gatepassDetails?.address || '--'}</span>
                    </div>
                  </div>

                  {popupData.gatepassDetails?.rejectionReason && (
                    <div style={{ margin: '12px 0', padding: '10px 14px', background: '#fef2f2', borderRadius: '8px', border: '1px solid #fecaca' }}>
                      <span style={{ fontSize: '12px', color: '#991b1b', fontWeight: 600, textTransform: 'uppercase' }}>Rejection Reason</span>
                      <p style={{ margin: '4px 0 0', color: '#dc2626', fontSize: '14px' }}>{popupData.gatepassDetails.rejectionReason}</p>
                    </div>
                  )}

                  <div className="gatepass-popup-utilization">
                    <h5>Scheduled Times</h5>
                    <div className="gatepass-popup-util-grid">
                      <div className="gatepass-popup-util-item exit">
                        <span className="gatepass-popup-util-label">Scheduled Exit</span>
                        <span className="gatepass-popup-util-value scheduled">
                          {formatScheduledDateTime(popupData.gatepassDetails?.dateOut, popupData.gatepassDetails?.timeOut)}
                        </span>
                      </div>
                      <div className="gatepass-popup-util-item entry">
                        <span className="gatepass-popup-util-label">Scheduled Entry</span>
                        <span className="gatepass-popup-util-value scheduled">
                          {formatScheduledDateTime(popupData.gatepassDetails?.dateIn, popupData.gatepassDetails?.timeIn)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="gatepass-popup-utilization">
                    <h5>Utilization Status</h5>
                    <div className="gatepass-popup-util-grid">
                      <div className="gatepass-popup-util-item exit">
                        <span className="gatepass-popup-util-label">Actual Exit</span>
                        <span className={`gatepass-popup-util-value ${!popupData.gatepassDetails?.actualExitAt ? 'not-done' : ''}`}>
                          {popupData.gatepassDetails?.actualExitAt ? formatDateTime(popupData.gatepassDetails.actualExitAt) : 'Not Done Yet'}
                        </span>
                      </div>
                      <div className="gatepass-popup-util-item entry">
                        <span className="gatepass-popup-util-label">Actual Entry</span>
                        <span className={`gatepass-popup-util-value ${!popupData.gatepassDetails?.actualEntryAt ? 'not-done' : ''}`}>
                          {popupData.gatepassDetails?.actualEntryAt ? formatDateTime(popupData.gatepassDetails.actualEntryAt) : 'Not Done Yet'}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="gatepass-popup-timeline">
                    <h5>Approval Timeline</h5>
                    <div className="gatepass-popup-timeline-list">
                      <div className="gatepass-popup-timeline-item">
                        <span className="gatepass-popup-timeline-stage">Applied</span>
                        <span className="gatepass-popup-timeline-time">
                          {formatDateTime(popupData.gatepassDetails?.appliedAt) || '--'}
                        </span>
                      </div>
                      {popupData.gatepassDetails?.approvedAt && (
                        <div className={`gatepass-popup-timeline-item ${popupData.gatepassDetails?.finalStatus === 'approved' ? 'approved' : 'rejected'}`}>
                          <span className="gatepass-popup-timeline-stage">
                            Hostel Office {popupData.gatepassDetails?.finalStatus === 'approved' ? 'Approved' : 'Rejected'}
                          </span>
                          <span className="gatepass-popup-timeline-time">
                            {formatDateTime(popupData.gatepassDetails.approvedAt)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  <GatepassExtraInfo details={popupData.gatepassDetails} />
                  <GatepassStageNotes details={popupData.gatepassDetails} />
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <StudentIdCardPopup
        student={selectedStudent}
        isOpen={showIdCard}
        onClose={() => setShowIdCard(false)}
      />
    </div>
  );
};

// Read-only decision register shared by Outstation and Mid/End Sem Break.
// This is intentionally separate from each section's workflow History view.
const GatepassDecisionLogRegisterView = ({
  title,
  gatepassPrefix,
  downloadName,
  fetchRegister,
  enableScheduledDateRange = false,
}) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [popupMessage, setPopupMessage] = useState('');

  const fetchLogs = async () => {
    if (enableScheduledDateRange && fromDate && toDate && fromDate > toDate) {
      setLogs([]);
      setPopupMessage('From date cannot be after To date.');
      return;
    }

    setLoading(true);
    try {
      const params = {};
      if (search.trim()) params.search = search.trim();
      if (status !== 'all') params.status = status;
      if (enableScheduledDateRange && fromDate) params.fromDate = fromDate;
      if (enableScheduledDateRange && toDate) params.toDate = toDate;
      const res = await fetchRegister(params);
      setLogs(res.data.logs || []);
    } catch (err) {
      setPopupMessage(err?.response?.data?.message || 'Failed to load log register');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(fetchLogs, 300);
    return () => clearTimeout(timer);
    // Imported API functions are stable; refetch only when a filter changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, status, fromDate, toDate, enableScheduledDateRange]);

  const formatLogTime = (value) => {
    if (!value) return '--';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '--';
    return date.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    }).toUpperCase();
  };

  const formatLogDate = (value) => {
    if (!value) return '--';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '--';
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
    });
  };

  const handleDownloadExcel = () => {
    if (!logs.length) return;

    const escapeCSV = (value) => {
      const stringValue = String(value ?? '');
      if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    };

    const headers = [
      'Sr',
      'Name',
      'Roll No',
      'Room',
      'Contact',
      'Gate Pass',
      'Time Out',
      'Exit Date',
      'Time In',
      'Entry Date',
      'Status',
    ];
    const rows = logs.map((log, index) => [
      index + 1,
      log.studentName || '',
      log.rollnumber || '',
      log.roomNumber || '',
      log.contact ? `="${log.contact}"` : '',
      log.gatePassNo || '',
      log.actualExitAt ? formatLogTime(log.actualExitAt) : '',
      log.actualExitAt ? formatLogDate(log.actualExitAt) : '',
      log.actualEntryAt ? formatLogTime(log.actualEntryAt) : '',
      log.actualEntryAt ? formatLogDate(log.actualEntryAt) : '',
      log.status || '',
    ].map(escapeCSV).join(','));

    const csvContent = `\uFEFF${[headers.join(','), ...rows].join('\r\n')}`;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    const dateSuffix = enableScheduledDateRange
      ? `${fromDate || 'all'}_to_${toDate || 'all'}`
      : new Date().toISOString().split('T')[0];
    link.setAttribute('download', `${downloadName}_${dateSuffix}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="os-section" style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', maxWidth: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap', gap: '8px', flexShrink: 0 }}>
        <h2 className="os-section-title" style={{ margin: 0 }}>{title}</h2>
        <div style={{ display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap' }}>
          {enableScheduledDateRange && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <label style={{ fontWeight: '500', fontSize: '14px', color: '#666' }}>FROM</label>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(event) => {
                    setPopupMessage('');
                    setFromDate(event.target.value);
                  }}
                  max={toDate || undefined}
                  style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px' }}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <label style={{ fontWeight: '500', fontSize: '14px', color: '#666' }}>TO</label>
                <input
                  type="date"
                  value={toDate}
                  onChange={(event) => {
                    setPopupMessage('');
                    setToDate(event.target.value);
                  }}
                  min={fromDate || undefined}
                  style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px' }}
                />
                {(fromDate || toDate) && (
                  <button
                    type="button"
                    onClick={() => {
                      setPopupMessage('');
                      setFromDate('');
                      setToDate('');
                    }}
                    style={{ padding: '8px 12px', background: '#f3f4f6', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}
                  >
                    All
                  </button>
                )}
              </div>
            </>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label style={{ fontWeight: '500', fontSize: '14px', color: '#666' }}>SEARCH</label>
            <input
              type="text"
              placeholder={`Name, Roll No., or ${gatepassPrefix}-00001`}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', minWidth: '200px' }}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label style={{ fontWeight: '500', fontSize: '14px', color: '#666' }}>STATUS</label>
            <select
              value={status}
              onChange={(event) => setStatus(event.target.value)}
              style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', minWidth: '180px' }}
            >
              <option value="all">All</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
          <button
            type="button"
            onClick={handleDownloadExcel}
            disabled={logs.length === 0}
            style={{
              background: logs.length === 0 ? '#9ca3af' : '#10b981',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '6px',
              cursor: logs.length === 0 ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            Download Excel
          </button>
        </div>
      </div>

      <div style={{ background: 'white', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <div style={{ overflow: 'auto', flex: 1 }}>
          <table style={{ width: '100%', minWidth: '1000px', borderCollapse: 'collapse', fontSize: '12px', tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: '42px' }} />
              <col style={{ width: '15%' }} />
              <col style={{ width: '10%' }} />
              <col style={{ width: '7%' }} />
              <col style={{ width: '11%' }} />
              <col style={{ width: '10%' }} />
              <col style={{ width: '9%' }} />
              <col style={{ width: '9%' }} />
              <col style={{ width: '9%' }} />
              <col style={{ width: '9%' }} />
              <col style={{ width: '9%' }} />
            </colgroup>
            <thead>
              <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #e5e7eb' }}>
                {['SR', 'NAME', 'ROLL NO.', 'ROOM', 'CONTACT', 'GATE PASS', 'TIME OUT', 'EXIT DATE', 'TIME IN', 'ENTRY DATE', 'STATUS'].map((heading) => (
                  <th key={heading} style={{ padding: '10px 6px', textAlign: heading === 'STATUS' ? 'center' : 'left', fontWeight: '600', color: '#374151', fontSize: '10px' }}>
                    {heading}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={11} style={{ padding: '40px', textAlign: 'center', color: '#666' }}>Loading...</td></tr>
              )}
              {!loading && logs.length === 0 && (
                <tr><td colSpan={11} style={{ padding: '40px', textAlign: 'center', color: '#666' }}>No log register records found</td></tr>
              )}
              {!loading && logs.map((log, index) => {
                const rowStyle = log.status === 'approved'
                  ? { backgroundColor: '#ecfdf5' }
                  : log.status === 'rejected'
                    ? { backgroundColor: '#fef2f2' }
                    : { backgroundColor: '#fffbeb' };
                return (
                  <tr key={log._id} style={{ borderBottom: '1px solid #e5e7eb', ...rowStyle }}>
                    <td style={{ padding: '8px 6px', color: '#ef4444', fontWeight: '500' }}>{index + 1}</td>
                    <td style={{ padding: '8px 6px', color: '#111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{log.studentName || '--'}</td>
                    <td style={{ padding: '8px 6px', color: '#111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{log.rollnumber || '--'}</td>
                    <td style={{ padding: '8px 6px', color: '#111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{log.roomNumber || '--'}</td>
                    <td style={{ padding: '8px 6px', color: '#111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{log.contact || '--'}</td>
                    <td style={{ padding: '8px 6px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <span style={{ background: '#eef2ff', color: '#1d4ed8', border: '1px solid #c7d2fe', padding: '3px 8px', borderRadius: '5px', fontSize: '11px', fontWeight: 600 }}>
                        {log.gatePassNo || '--'}
                      </span>
                    </td>
                    <td style={{ padding: '8px 6px', color: '#111', whiteSpace: 'nowrap' }}>{formatLogTime(log.actualExitAt)}</td>
                    <td style={{ padding: '8px 6px', color: '#111', whiteSpace: 'nowrap' }}>{formatLogDate(log.actualExitAt)}</td>
                    <td style={{ padding: '8px 6px', color: log.actualEntryAt ? '#059669' : '#999', whiteSpace: 'nowrap' }}>{formatLogTime(log.actualEntryAt)}</td>
                    <td style={{ padding: '8px 6px', color: log.actualEntryAt ? '#059669' : '#999', whiteSpace: 'nowrap' }}>{formatLogDate(log.actualEntryAt)}</td>
                    <td style={{ padding: '8px 6px', textAlign: 'center' }}>
                      <span style={{
                        display: 'inline-block',
                        padding: '3px 8px',
                        borderRadius: '999px',
                        fontSize: '10px',
                        fontWeight: 700,
                        color: log.status === 'approved' ? '#047857' : log.status === 'rejected' ? '#b91c1c' : '#b45309',
                        background: log.status === 'approved' ? '#d1fae5' : log.status === 'rejected' ? '#fee2e2' : '#fef3c7',
                      }}>
                        {(log.status || '--').toUpperCase()}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <PopupBox message={popupMessage} onClose={() => setPopupMessage('')} />
    </div>
  );
};

// ==================== BANGALORE GATEPASS VIEWS ====================

const BGRequestsView = ({ onViewDetails }) => {
  const [gatepasses, setGatepasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showIdCard, setShowIdCard] = useState(false);

  const handleProfileClick = (student, gatepass) => {
    setSelectedStudent({
      ...student,
      name: gatepass.studentName,
      rollnumber: gatepass.rollnumber,
      branch: gatepass.branch || gatepass.department,
      department: gatepass.department,
      contact: gatepass.contact,
      roomNumber: gatepass.roomNumber,
      hostelName: gatepass.hostelName,
      course: gatepass.course,
    });
    setShowIdCard(true);
  };

  const fetchPending = async () => {
    setLoading(true);
    try {
      const res = await getHostelOfficeBGPendingGatepasses();
      setGatepasses(res.data.gatepasses || []);
      setError('');
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load Bangalore requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPending();
  }, []);

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    let hours = d.getHours();
    const mins = String(d.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    return `${day}/${month}/${year},  ${hours}:${mins} ${ampm}`;
  };

  const sortedGatepasses = [...gatepasses].sort((a, b) =>
    new Date(a.createdAt) - new Date(b.createdAt)
  );

  const normalizedSearch = search.trim().toLowerCase();
  const filteredGatepasses = normalizedSearch
    ? sortedGatepasses.filter((gp) => {
        const name = gp.studentName || gp.student?.name || '';
        const roll = gp.rollnumber || gp.student?.rollnumber || '';
        return `${name} ${roll}`.toLowerCase().includes(normalizedSearch);
      })
    : sortedGatepasses;

  return (
    <div className="os-section">
      <div className="os-history-header-row">
        <h2 className="os-section-title">Pending Bangalore Transfer Requests</h2>
        <form className="os-search-form" onSubmit={(e) => e.preventDefault()}>
          <input
            type="text"
            placeholder="Search by name or roll number..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="os-search-input"
            aria-label="Search by name or roll number"
          />
        </form>
      </div>

      {loading && <div className="os-loading">Loading requests...</div>}
      {error && <div className="os-error">{error}</div>}

      {!loading && gatepasses.length === 0 && (
        <div className="os-empty">No pending Bangalore transfer requests</div>
      )}

      {!loading && gatepasses.length > 0 && filteredGatepasses.length === 0 && (
        <div className="os-empty">No matches found</div>
      )}

      <div className="os-cards-grid">
        {filteredGatepasses.map((gp) => (
          <div key={gp._id} className="os-request-card">
            <div
              className="os-card-avatar profile-pic-hover"
              onClick={() => handleProfileClick(gp.student, gp)}
              title="Click to view GoThru ID Card"
            >
              {gp.student?.imageUrl ? (
                <img
                  src={getImageUrl(gp.student.imageUrl)}
                  alt={gp.studentName}
                  className="os-card-avatar-img"
                />
              ) : (
                <span style={{ fontSize: '20px', color: '#fff' }}>●</span>
              )}
            </div>
            <div className="os-card-info">
              <div className="os-card-name">{gp.studentName}</div>
              <div className="os-card-details">
                <span>{gp.course || '—'}</span>
                <span>{gp.rollnumber}</span>
              </div>
              <div className="os-card-branch">{gp.hostelName || gp.branch || gp.department || '—'}</div>
              <div className="os-card-applied">Exit: {formatScheduledDateTime(gp.dateOut, gp.timeOut)}</div>
              <div className="os-card-applied">Applied: {formatDateTime(gp.createdAt)}</div>
            </div>
            <button className="os-view-btn" onClick={() => onViewDetails(gp._id)}>
              View Details
            </button>
          </div>
        ))}
      </div>

      <StudentIdCardPopup
        student={selectedStudent}
        isOpen={showIdCard}
        onClose={() => setShowIdCard(false)}
      />
    </div>
  );
};

const BGGatepassDetailsView = ({ gatepassId, onBack }) => {
  const [gatepass, setGatepass] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [popupMessage, setPopupMessage] = useState('');
  const [deciding, setDeciding] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ open: false, decision: null });
  const [showIdCard, setShowIdCard] = useState(false);

  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  useEffect(() => {
    (async () => {
      try {
        setError('');
        const res = await getHostelOfficeBGGatepassDetails(gatepassId);
        setGatepass(res.data.gatepass || null);
      } catch (err) {
        setError(err?.response?.data?.message || 'Failed to fetch details');
      } finally {
        setLoading(false);
      }
    })();
  }, [gatepassId]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '--';
    const parts = dateStr.split('-');
    return parts.length === 3 ? `${parseInt(parts[2], 10)} ${monthNames[parseInt(parts[1], 10) - 1]} ${parts[0]}` : dateStr;
  };

  const formatTime12hr = (timeStr) => {
    if (!timeStr) return '';
    const parts = timeStr.split(':');
    if (parts.length < 2) return timeStr;
    let hours = parseInt(parts[0], 10);
    const minutes = parts[1];
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    return `${hours}:${minutes} ${ampm}`;
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const day = d.getDate();
    const month = monthNames[d.getMonth()];
    let hours = d.getHours();
    const mins = String(d.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    return `${day} ${month}, ${hours}:${mins} ${ampm}`;
  };

  const openConfirmModal = (decision) => setConfirmModal({ open: true, decision });
  const closeConfirmModal = () => setConfirmModal({ open: false, decision: null });

  const handleDecision = async (decision) => {
    setDeciding(true);
    try {
      const res = await decideHostelOfficeBGGatepass({ gatepassId, decision });
      closeConfirmModal();
      setPopupMessage(res.data.message);
      setTimeout(() => onBack(), 1500);
    } catch (err) {
      setPopupMessage(err?.response?.data?.message || 'Failed to process decision');
      closeConfirmModal();
    } finally {
      setDeciding(false);
    }
  };

  if (loading) return <div className="os-loading">Loading details...</div>;
  if (error) return <div className="os-error">{error}</div>;
  if (!gatepass) return <div className="os-error">Gatepass not found</div>;

  const student = gatepass.student || {};

  return (
    <div className="os-details-view">
      <button className="os-back-btn" onClick={onBack}>
        ← Back to Requests
      </button>

      <h2 className="os-section-title">Bangalore Transfer Details</h2>

      <div className="os-details-card">
        <div className="os-student-info-header">
          <div
            className="os-student-avatar-large profile-pic-hover"
            onClick={() => setShowIdCard(true)}
            title="Click to view GoThru ID Card"
          >
            {student?.imageUrl ? (
              <img src={getImageUrl(student.imageUrl)} alt={gatepass.studentName} />
            ) : (
              <span className="os-avatar-placeholder">{gatepass.studentName?.charAt(0)?.toUpperCase() || '?'}</span>
            )}
          </div>
          <div className="os-student-header-info">
            <h3 className="os-student-name-large">{gatepass.studentName}</h3>
            <span className="os-student-roll-large">{gatepass.rollnumber}</span>
          </div>
        </div>

        <div className="os-details-grid">
          <div className="os-detail-item">
            <span className="os-detail-label">Department</span>
            <span className="os-detail-value">{gatepass.department || student.department || '--'}</span>
          </div>
          <div className="os-detail-item">
            <span className="os-detail-label">Branch</span>
            <span className="os-detail-value">{gatepass.branch || student.branch || '--'}</span>
          </div>
          <div className="os-detail-item">
            <span className="os-detail-label">Course</span>
            <span className="os-detail-value">{gatepass.course || student.course || '--'}</span>
          </div>
          <div className="os-detail-item">
            <span className="os-detail-label">Room / Hostel</span>
            <span className="os-detail-value">{gatepass.roomNumber || student.roomNumber || '--'} / {gatepass.hostelName || student.hostelName || '--'}</span>
          </div>
          <div className="os-detail-item">
            <span className="os-detail-label">Contact</span>
            <span className="os-detail-value">{gatepass.contact || '--'}</span>
          </div>
          <div className="os-detail-item">
            <span className="os-detail-label">Exit Date</span>
            <span className="os-detail-value">{formatDate(gatepass.dateOut)} {formatTime12hr(gatepass.timeOut)}</span>
          </div>
          <div className="os-detail-item">
            <span className="os-detail-label">Bangalore Address</span>
            <span className="os-detail-value">{gatepass.address || '--'}</span>
          </div>
          <div className="os-detail-item">
            <span className="os-detail-label">Reason</span>
            <span className="os-detail-value">{gatepass.reason || '--'}</span>
          </div>
          <div className="os-detail-item">
            <span className="os-detail-label">Current Campus</span>
            <span className="os-detail-value" style={{ textTransform: 'capitalize' }}>{student.campus || 'jais'}</span>
          </div>
          <div className="os-detail-item">
            <span className="os-detail-label">Status</span>
            <span className="os-detail-value" style={{ textTransform: 'capitalize' }}>{gatepass.status}</span>
          </div>
        </div>

        <div className="os-approval-timeline">
          <h4>Approval Timeline</h4>
          <div className="os-timeline-item">
            <span className="os-timeline-label">Student Applied</span>
            <span className="os-timeline-value">{formatDateTime(gatepass.createdAt)}</span>
          </div>
          <div className="os-timeline-item">
            <span className="os-timeline-label">Direct to Hostel Office</span>
            <span className="os-timeline-value" style={{ color: '#e67e22', fontWeight: 500 }}>Bangalore Transfer — Hostel Office decides</span>
          </div>
          {gatepass.decidedAt && (
            <div className="os-timeline-item">
              <span className="os-timeline-label">Decided</span>
              <span className="os-timeline-value">{formatDateTime(gatepass.decidedAt)}</span>
            </div>
          )}
          {gatepass.rejectionReason && (
            <div className="os-timeline-item">
              <span className="os-timeline-label">Rejection Reason</span>
              <span className="os-timeline-value">{gatepass.rejectionReason}</span>
            </div>
          )}
        </div>

        {gatepass.status === 'pending' && (
          <div className="os-action-btns">
            <button
              className="os-reject-btn"
              onClick={() => openConfirmModal('rejected')}
              disabled={deciding}
            >
              Reject
            </button>
            <button
              className="os-approve-btn"
              onClick={() => openConfirmModal('approved')}
              disabled={deciding}
            >
              Approve — Transfer to Bangalore
            </button>
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={confirmModal.open}
        onClose={closeConfirmModal}
        onConfirm={() => handleDecision(confirmModal.decision)}
        decision={confirmModal.decision}
        studentName={gatepass?.studentName}
        rollNumber={gatepass?.rollnumber}
        isLocal={false}
        reasonOfLeave={gatepass?.reason || 'Permanent transfer to Bangalore campus'}
        isProcessing={deciding}
      />

      <StudentIdCardPopup
        student={gatepass ? {
          ...student,
          name: gatepass.studentName,
          rollnumber: gatepass.rollnumber,
          branch: gatepass.branch || gatepass.department,
          department: gatepass.department,
          contact: gatepass.contact,
          roomNumber: gatepass.roomNumber,
          hostelName: gatepass.hostelName,
          course: gatepass.course,
        } : null}
        isOpen={showIdCard}
        onClose={() => setShowIdCard(false)}
      />

      <PopupBox message={popupMessage} onClose={() => setPopupMessage('')} />
    </div>
  );
};

const BGLogRegisterView = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [revertingId, setRevertingId] = useState(null);
  const [popupMessage, setPopupMessage] = useState('');

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search.trim()) params.search = search.trim();
      if (status !== 'all') params.status = status;
      const res = await getHostelOfficeBGLogRegister(params);
      setLogs(res.data.logs || []);
    } catch (err) {
      setPopupMessage(err?.response?.data?.message || 'Failed to load log register');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(fetchLogs, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, status]);

  const formatLogDateTime = (dateStr) => {
    if (!dateStr) return '--';
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return '--';
    const datePart = d.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: '2-digit' });
    const timePart = d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }).toUpperCase();
    return `${datePart}, ${timePart}`;
  };

  const formatActualExitTime = (value) => {
    if (!value) return '--';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '--';
    return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }).toUpperCase();
  };

  const formatActualExitDate = (value) => {
    if (!value) return '--';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '--';
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: '2-digit' });
  };

  const handleRevert = async (log) => {
    if (!log.student?._id && !log.student) return;
    const studentId = typeof log.student === 'object' ? log.student._id : log.student;
    const confirmMsg = `Revert ${log.studentName} (${log.rollnumber}) back to Jais campus? Their Jais account will become active again.`;
    if (!window.confirm(confirmMsg)) return;
    setRevertingId(log._id);
    try {
      await revertBangaloreStudent(studentId);
      setPopupMessage(`${log.studentName} reverted to Jais campus.`);
      await fetchLogs();
    } catch (err) {
      setPopupMessage(err?.response?.data?.message || 'Failed to revert student');
    } finally {
      setRevertingId(null);
    }
  };

  const handleDownloadExcel = () => {
    if (!logs.length) return;
    const headers = ['Sr', 'Name', 'Roll No', 'Room', 'Contact', 'Gate Pass', 'Time Out', 'Exit Date'];
    const escapeCSV = (val) => {
      const str = String(val ?? '');
      if (str.includes(',') || str.includes('"') || str.includes('\n')) return `"${str.replace(/"/g, '""')}"`;
      return str;
    };
    const rows = logs.map((log, idx) => [
      idx + 1,
      log.studentName || '',
      log.rollnumber || '',
      log.roomNumber || '',
      log.contact ? `="${log.contact}"` : '',
      log.gatePassNo || '--',
      log.actualExitAt ? formatActualExitTime(log.actualExitAt) : '',
      log.actualExitAt ? formatActualExitDate(log.actualExitAt) : '',
    ].map(escapeCSV).join(','));

    const BOM = '\uFEFF';
    const csvContent = BOM + [headers.join(','), ...rows].join('\r\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `BangaloreLogRegister_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="os-section" style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', maxWidth: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap', gap: '8px', flexShrink: 0 }}>
        <h2 className="os-section-title" style={{ margin: 0 }}>Bangalore Transfer Log Register</h2>
        <div style={{ display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label style={{ fontWeight: '500', fontSize: '14px', color: '#666' }}>SEARCH</label>
            <input
              type="text"
              placeholder="Name, Roll No., or BG-00001"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', minWidth: '200px' }}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label style={{ fontWeight: '500', fontSize: '14px', color: '#666' }}>STATUS</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', minWidth: '180px' }}
            >
              <option value="all">Approved + Rejected</option>
              <option value="approved">✅ Approved (Transferred)</option>
              <option value="rejected">❌ Rejected</option>
            </select>
          </div>
          <button
            onClick={handleDownloadExcel}
            disabled={logs.length === 0}
            style={{
              background: logs.length === 0 ? '#9ca3af' : '#10b981',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '6px',
              cursor: logs.length === 0 ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            📥 Download Excel
          </button>
        </div>
      </div>

      <div style={{ background: 'white', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <div style={{ overflowY: 'auto', overflowX: 'hidden', flex: 1 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: '42px' }} />
              <col style={{ width: '18%' }} />
              <col style={{ width: '11%' }} />
              <col style={{ width: '8%' }} />
              <col style={{ width: '12%' }} />
              <col style={{ width: '12%' }} />
              <col style={{ width: '10%' }} />
              <col style={{ width: '14%' }} />
              <col style={{ width: '12%' }} />
            </colgroup>
            <thead>
              <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #e5e7eb' }}>
                <th style={{ padding: '10px 6px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '10px' }}>SR</th>
                <th style={{ padding: '10px 6px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '10px' }}>NAME</th>
                <th style={{ padding: '10px 6px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '10px' }}>ROLL NO.</th>
                <th style={{ padding: '10px 6px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '10px' }}>ROOM</th>
                <th style={{ padding: '10px 6px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '10px' }}>CONTACT</th>
                <th style={{ padding: '10px 6px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '10px' }}>GATE PASS</th>
                <th style={{ padding: '10px 6px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '10px' }}>TIME OUT</th>
                <th style={{ padding: '10px 6px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '10px' }}>EXIT DATE</th>
                <th style={{ padding: '10px 6px', textAlign: 'center', fontWeight: '600', color: '#374151', fontSize: '10px' }}>ACTION</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={9} style={{ padding: '40px', textAlign: 'center', color: '#666' }}>Loading...</td></tr>
              )}
              {!loading && logs.length === 0 && (
                <tr><td colSpan={9} style={{ padding: '40px', textAlign: 'center', color: '#666' }}>No Bangalore transfer records</td></tr>
              )}
              {!loading && logs.map((log, idx) => {
                const rowStyle = log.status === 'approved'
                  ? { backgroundColor: '#ecfdf5' }
                  : log.status === 'rejected'
                    ? { backgroundColor: '#fef2f2' }
                    : {};
                return (
                  <tr key={log._id} style={{ borderBottom: '1px solid #e5e7eb', ...rowStyle }}>
                    <td style={{ padding: '8px 6px', color: '#ef4444', fontWeight: '500', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{idx + 1}</td>
                    <td style={{ padding: '8px 6px', color: '#111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{log.studentName || '--'}</td>
                    <td style={{ padding: '8px 6px', color: '#111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{log.rollnumber || '--'}</td>
                    <td style={{ padding: '8px 6px', color: '#111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{log.roomNumber || '--'}</td>
                    <td style={{ padding: '8px 6px', color: '#111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{log.contact || '--'}</td>
                    <td style={{ padding: '8px 6px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <span style={{ background: '#eef2ff', color: '#1d4ed8', border: '1px solid #c7d2fe', padding: '3px 8px', borderRadius: '5px', fontSize: '11px', fontWeight: 600 }}>
                        {log.gatePassNo || '--'}
                      </span>
                    </td>
                    <td style={{ padding: '8px 6px', color: '#111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{formatActualExitTime(log.actualExitAt)}</td>
                    <td style={{ padding: '8px 6px', color: '#111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{formatActualExitDate(log.actualExitAt)}</td>
                    <td style={{ padding: '8px 6px', textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {log.status === 'approved' && log.student?.campus === 'bangalore' ? (
                        <button
                          onClick={() => handleRevert(log)}
                          disabled={revertingId === log._id}
                          title="Bring student back to Jais campus"
                          style={{
                            background: '#fff7ed',
                            color: '#c2410c',
                            border: '1px solid #fed7aa',
                            padding: '4px 8px',
                            borderRadius: '5px',
                            fontSize: '11px',
                            fontWeight: 600,
                            cursor: revertingId === log._id ? 'wait' : 'pointer',
                            fontFamily: 'inherit',
                          }}
                        >
                          {revertingId === log._id ? '...' : '↺ Revert'}
                        </button>
                      ) : (
                        <span style={{ color: '#999', fontSize: '11px' }}>--</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <PopupBox message={popupMessage} onClose={() => setPopupMessage('')} />
    </div>
  );
};

export default HostelOfficePage;

